import React, { useEffect, useRef, useState, useId } from 'react';
import axios from 'axios';
import { refusalMessage, currentLang } from './docText';
import { useTranslation } from 'react-i18next';
import { useDarkMode } from './DarkModeContext';
import WorkflowDiagram from './WorkflowDiagram';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';

const API = '/api';

// PAYROLLPICK: searchable multi-select employee picker for the Payroll COO step's "New joiners"
// and "Leavers" fields (previously free text). Reuses the same client-side name-filter +
// dropdown pattern as AddEmployee.js's manager selector rather than a new dependency. Stores and
// returns selected employees as {id, name}; the caller decides what to persist (we persist the
// joined NAMES for the Financial Controller's readability - see handlePayrollChanges).
// PAYROLLROWS: a single-select employee name search for one row cell. Same shape as the existing
// EmployeePicker below (type to filter, click to choose, clear to re-search) but bound to ONE row
// rather than a multi-select list, which is what a per-person row needs. `options` is already
// entity-scoped by the caller (payroll-employee-search), so this component never decides scope.
//
// The COO may also type a name that matches nobody: the cell keeps what he typed, and the row is
// submitted with employee_name set and employee_id null. That is deliberate - a joiner may not
// exist in the directory yet on the day he sends the month's changes.
function EmployeeNameSearch({ options, value, employeeId, onPick, placeholder, isDarkMode }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const matches = query.trim().length < 1 ? [] : options.filter(o =>
    o.name.toLowerCase().includes(query.toLowerCase())
  ).slice(0, 8);

  const border = isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd';
  const bg = isDarkMode ? '#1a2744' : '#fff';
  const fg = isDarkMode ? 'rgba(255,255,255,0.85)' : '#333';

  // PAYGRID: the grid now owns this cell's width (column 1 of PAYROLL_ROW_GRID), so this
  // component no longer declares a flex basis of its own. minWidth 0 lets it shrink inside its
  // track rather than forcing the row wider.
  return (
    <div ref={boxRef} style={{ position: 'relative', minWidth: 0 }}>
      <input
        type="text"
        value={query || value || ''}
        onChange={e => {
          setQuery(e.target.value);
          setOpen(true);
          // Typing replaces any previously picked person: the name is kept, the id is dropped
          // unless a real match is chosen again, so an id can never point at the wrong person.
          onPick({ employee_name: e.target.value, employee_id: null });
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        style={{
          width: '100%', boxSizing: 'border-box', fontSize: '13px', padding: '7px 9px',
          borderRadius: '6px', border: `1px solid ${border}`, background: bg, color: fg,
        }}
      />
      {open && matches.length > 0 && (
        <div style={{
          position: 'absolute', zIndex: 5, top: '100%', left: 0, right: 0, background: bg,
          border: `1px solid ${border}`, borderRadius: '6px', marginTop: '2px', maxHeight: '180px',
          overflowY: 'auto',
        }}>
          {matches.map(o => (
            <div
              key={o.id}
              onClick={() => {
                onPick({ employee_name: o.name, employee_id: o.id });
                setQuery('');
                setOpen(false);
              }}
              style={{ padding: '7px 9px', fontSize: '13px', cursor: 'pointer', color: fg }}
            >
              {o.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// PAYROLLROWS: one repeatable category block - a labelled list of rows, a Remove control per row
// and an Add control beneath. The CELLS of a row are supplied by the caller via renderCells, so
// all three categories share this add/remove/empty-state behaviour without three near-identical
// copies of it.
// PAYGRID: the single column template every payroll category row renders against. Declared once
// here rather than as per-cell inline widths inside each category, which is what lets the columns
// line up across all five. The name column is wider because it holds a search control; the three
// value columns are identical so a date, an amount and a comment all land in the same places.
const PAYROLL_ROW_GRID = 'minmax(0, 1.6fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) auto';


function PayrollRowCategory({ label, addLabel, rows, onChange, blankRow, renderCells, isDarkMode, textMuted, border }) {
  const update = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () => onChange([...rows, { ...blankRow }]);

  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ fontSize: '12px', color: textMuted, marginBottom: '4px' }}>{label}</div>
      {rows.length === 0 && (
        <div style={{ fontSize: '12px', color: textMuted, fontStyle: 'italic', marginBottom: '4px' }}>
          None this month
        </div>
      )}
      {/* PAYGRID (confirmed by Marie-Lynne): ONE column template, defined here once, that all
          five categories render against - replacing the five separately declared flex layouts
          whose per-cell widths meant nothing lined up vertically between categories.

          Column 1 is always the employee, columns 2 to 4 are that category's value cells, and the
          Remove control has its own fixed last column. A category with fewer cells LEAVES THE
          REMAINING COLUMNS EMPTY rather than stretching to fill them (Marie-Lynne's confirmed
          choice): stretching is exactly what made a 2-cell Intern payments row far wider per cell
          than a 4-cell New joiners row, which is the misalignment being fixed. Visible white space
          on a short row is the deliberate trade for every category lining up.

          minmax(0, ...) on each track lets a cell shrink below its content width instead of
          forcing the row wider, so long placeholder text cannot push the grid out of alignment.
          The widest category today carries 4 cells, so the template is name + 3 + the button. */}
      {rows.map((row, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: PAYROLL_ROW_GRID, gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
          {renderCells(row, (patch) => update(i, patch))}
          <button
            type="button"
            onClick={() => remove(i)}
            aria-label={`Remove ${label} row ${i + 1}`}
            style={{
              background: 'transparent', color: '#A63A3A', border: `1px solid ${border}`,
              borderRadius: '6px', padding: '6px 10px', fontSize: '12px', cursor: 'pointer',
            }}
          >
            Remove
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        style={{
          // DATELABELBLUE: this Add control was one of the non-status gold call sites that
          // STATUSPALETTE deliberately EXCLUDED (it recoloured the status pills only, and SP-11
          // counted this one as an intentional leftover). Marie-Lynne has now asked for the
          // payroll form's Add buttons in blue too, so it takes the SAME light-mode in_progress
          // values already used by the pills on this screen - #12447F text on a #5A82B4 border -
          // rather than a new blue invented here.
          background: 'transparent', color: '#12447F', border: '1px solid #5A82B4',
          borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
        }}
      >
        {/* PAYFIELDS: the singular is passed in explicitly. Deriving it by stripping a trailing
            "s" turned the renamed "Bonus" category into "Add bonu" - a label ending in s is not
            necessarily a plural. */}
        Add {addLabel || label.toLowerCase().replace(/s$/, '')}
      </button>
    </div>
  );
}

// PAYROLLROWS: the shared read-only rendering of the COO's submitted change rows, used by BOTH
// the Financial Controller's processing step and the COO's own final approval step, so the two can
// never drift apart. Each category becomes a labelled block with one line per person; a category
// with no rows is omitted entirely rather than shown empty.
// PAYATTACH / PAYPREVIEW: the one control that opens a payroll attachment, used for BOTH
// directions of the same gap - the COO's salary-change document and the Financial Controller's own
// provider report. It uses the purpose-built /workflows/{id}/attachment/{docId} route, NOT the
// general /documents/{id}/view endpoint, which refuses the finance role and would have had to be
// widened to every employee document to serve this one need.
//
// FETCHED AS A BLOB, not linked with a bare href. This reuses the pattern EmployeeProfile,
// EmployeeView, MigrationConfirmation and ComplianceModule all already use for documents:
// axios.get(..., { responseType: 'blob' }), then an object URL. That is not a stylistic choice -
// auth on this portal is a Bearer header set on axios defaults, so a plain <a href> sends NO token
// and the request fails. An earlier version of this component did exactly that.
//
// PREVIEW vs DOWNLOAD: the metadata endpoint reports `can_preview`, computed server side from the
// SAME INLINE_SAFE_MIME_TYPES the byte endpoint uses for its Content-Disposition, so the control
// offered can never disagree with what the server will actually do. Preview opens the object URL
// in a new tab; Download uses the `download` attribute with the real file name. DOWNLOAD IS
// OFFERED FOR EVERY FILE, including types that cannot be previewed - which is the point of
// splitting them.
function PayrollAttachmentLink({ instanceId, documentId, label }) {
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!instanceId || !documentId) return;
    let live = true;
    axios.get(`${API}/workflows/${instanceId}/attachment/${documentId}/meta`)
      .then(res => { if (live) setMeta(res.data || null); })
      // A failed metadata read must not hide the attachment: the download control below falls
      // back to a generic name, so the file is still reachable.
      .catch(() => { if (live) setMeta(null); });
    return () => { live = false; };
  }, [instanceId, documentId]);

  if (!instanceId || !documentId) return null;

  const fetchBlob = async () => {
    const res = await axios.get(
      `${API}/workflows/${instanceId}/attachment/${documentId}`, { responseType: 'blob' });
    return URL.createObjectURL(res.data);
  };

  const withBlob = async (use) => {
    setBusy(true);
    setError(null);
    try {
      const url = await fetchBlob();
      use(url);
      // Revoked on a delay rather than immediately: the new tab or the download needs the URL to
      // still resolve after this handler returns. Same 60s the profile's own opener uses.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setError('Could not open that document.');
    } finally {
      setBusy(false);
    }
  };

  const openPreview = () => withBlob((url) => {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  });

  const download = () => withBlob((url) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = (meta && meta.file_name) || 'attachment';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  });

  const linkStyle = {
    fontSize: '12px', color: '#12447F', textDecoration: 'underline', marginLeft: '6px',
    background: 'none', border: 'none', padding: 0, cursor: busy ? 'wait' : 'pointer',
  };

  return (
    <>
      {meta && meta.file_name ? (
        <span style={{ fontSize: '12px', marginLeft: '6px' }}>{meta.file_name}</span>
      ) : null}
      {/* Preview only for a type the SERVER will serve inline; download always. */}
      {meta && meta.can_preview ? (
        <button type="button" onClick={openPreview} disabled={busy} style={linkStyle}>
          {label ? `Preview ${label}` : 'Preview'}
        </button>
      ) : null}
      <button type="button" onClick={download} disabled={busy} style={linkStyle}>
        Download
      </button>
      {error ? (
        <span style={{ fontSize: '12px', color: '#A63A3A', marginLeft: '6px' }}>{error}</span>
      ) : null}
    </>
  );
}


// STEPEVIDENCE: a step's uploaded evidence, fetched through the portal's own scoped route.
//
// Deliberately the SAME shape as PayrollAttachmentLink above (read meta, fetch bytes as a blob so
// the Authorization header travels, Preview only where the SERVER will serve inline, Download
// always, revoke on the same 60s delay). The ONE difference is the address: a payroll attachment
// is a Document row named by id, while step evidence lives in step_state and is named by STEP
// KEY. No second mechanism is invented here.
export function StepEvidenceLink({ instanceId, stepKey, label }) {
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!instanceId || !stepKey) return undefined;
    let live = true;
    axios.get(`${API}/workflows/${instanceId}/step-evidence/${stepKey}/meta`)
      .then(res => { if (live) setMeta(res.data || null); })
      // A failed metadata read must not hide the evidence: the download control below still
      // offers it under a generic name.
      .catch(() => { if (live) setMeta(null); });
    return () => { live = false; };
  }, [instanceId, stepKey]);

  if (!instanceId || !stepKey) return null;

  const withBlob = async (use) => {
    setBusy(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API}/workflows/${instanceId}/step-evidence/${stepKey}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      use(url);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not open that document.');
    } finally {
      setBusy(false);
    }
  };

  const openPreview = () => withBlob((url) => {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  });

  const download = () => withBlob((url) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = (meta && meta.file_name) || 'evidence';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  });

  const linkStyle = {
    fontSize: '12px', color: '#12447F', textDecoration: 'underline', marginLeft: '6px',
    background: 'none', border: 'none', padding: 0, cursor: busy ? 'wait' : 'pointer',
  };

  // A legacy row whose item id was never captured cannot be fetched. Say so rather than offering
  // a control that would fail.
  if (meta && meta.retrievable === false) {
    return (
      <span style={{ fontSize: '12px', marginLeft: '6px', color: '#8a6a1f' }}>
        Evidence not retrievable - contact Finance.
      </span>
    );
  }

  return (
    <>
      {meta && meta.can_preview ? (
        <button type="button" onClick={openPreview} disabled={busy} style={linkStyle}>
          {label ? `Preview ${label}` : 'Preview'}
        </button>
      ) : null}
      <button type="button" onClick={download} disabled={busy} style={linkStyle}>
        Download
      </button>
      {error ? (
        <span style={{ fontSize: '12px', color: '#A63A3A', marginLeft: '6px' }}>{error}</span>
      ) : null}
    </>
  );
}


function PayrollChangeSummary({ fd, textMuted, instanceId }) {
  const CATEGORIES = [
    ['new_joiners', 'New joiners'],
    ['leavers', 'Leavers'],
    ['salary_changes', 'Salary changes'],
    // PAYROLLBONUS: the fourth section, rendered by the SAME shared component as the other three
    // (used by both the FC's processing step and the COO's approval step), so the four can never
    // drift apart in wording or layout.
    // PAYFIELDS: renamed from "Bonus or premium" to just "Bonus" (her preference). The KEY is
    // deliberately unchanged - renaming it would break every stored record for a label change.
    ['bonus_premium', 'Bonus'],
    // PAYFIELDS: interns get their own category - they do not follow the joiner or leaver flow.
    ['intern_payments', 'Intern payments'],
  ];
  const present = CATEGORIES.filter(([k]) => Array.isArray(fd[k]) && fd[k].length > 0);
  // PAYFIELDS: the form's own general comment is not a category of rows, so it renders after
  // them as its own block. A record with only a comment still shows it rather than reading as
  // "No changes recorded".
  const comment = (fd.general_comment || '').trim();
  if (present.length === 0 && !comment) return <div>No changes recorded</div>;
  return (
    <>
      {present.map(([k, label]) => (
        <div key={k} style={{ marginBottom: '6px' }}>
          <div style={{ color: textMuted }}>{label}:</div>
          {fd[k].map((row, i) => (
            <div key={i} style={{ marginLeft: '10px' }}>
              {/* PAYATTACH: formatPayrollRow still returns a plain STRING for every line, exactly
                  as before - it is used by other call sites and by tests that assert its text. The
                  link is rendered BESIDE that string here rather than inside it, which is what
                  lets an attachment become clickable without changing the formatter's contract. */}
              {formatPayrollRow(k, row)}
              {row && row.document_id ? (
                <PayrollAttachmentLink instanceId={instanceId} documentId={row.document_id} />
              ) : null}
            </div>
          ))}
        </div>
      ))}
      {comment ? (
        <div style={{ marginBottom: '6px' }}>
          <div style={{ color: textMuted }}>Comment:</div>
          <div style={{ marginLeft: '10px', whiteSpace: 'pre-wrap' }}>{comment}</div>
        </div>
      ) : null}
    </>
  );
}

// SUPERSEDED by PAYROLLROWS but deliberately KEPT rather than deleted, matching this codebase's
// habit of leaving reusable pieces in place (see PayrollProcessingForm in routers/workflows.py).
// This is the MULTI-select picker the payroll form used when joiners/leavers were comma-joined
// name lists; the per-row single-select EmployeeNameSearch above replaced it for that form. No
// caller today.
function EmployeePicker({ options, selected, onChange, placeholder, isDarkMode }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selectedIds = new Set(selected.map(s => s.id));
  const matches = query.trim().length < 1 ? [] : options.filter(o =>
    o.name.toLowerCase().includes(query.toLowerCase()) && !selectedIds.has(o.id)
  ).slice(0, 8);

  const add = (o) => { onChange([...selected, o]); setQuery(''); setOpen(false); };
  const remove = (id) => onChange(selected.filter(s => s.id !== id));

  const border = isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd';
  const bg = isDarkMode ? '#1a2744' : '#fff';
  const fg = isDarkMode ? 'rgba(255,255,255,0.85)' : '#333';

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '6px' }}>
          {selected.map(s => (
            <span key={s.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', background: isDarkMode ? 'rgba(200,168,75,0.15)' : '#f3ead3', color: isDarkMode ? '#c8a84b' : '#8a6a1f', borderRadius: '999px', padding: '2px 8px' }}>
              {s.name}
              <button type="button" onClick={() => remove(s.id)} title="Remove"
                style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '13px', lineHeight: 1, padding: 0 }}>×</button>
            </span>
          ))}
        </div>
      )}
      <input
        type="text"
        value={query}
        placeholder={placeholder}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        style={{ fontSize: '13px', padding: '7px 9px', borderRadius: '6px', border: `1px solid ${border}`, background: bg, color: fg, width: '100%', boxSizing: 'border-box' }}
      />
      {open && matches.length > 0 && (
        <div style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0, background: bg, border: `1px solid ${border}`, borderRadius: '6px', marginTop: '2px', maxHeight: '180px', overflowY: 'auto' }}>
          {matches.map(o => (
            <div key={o.id} onClick={() => add(o)}
              style={{ padding: '7px 9px', fontSize: '13px', color: fg, cursor: 'pointer' }}
              onMouseDown={e => e.preventDefault()}>
              {o.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// BACKFILLGUARD: skipped_backfill is an audit-only record for a period whose due date had already
// passed when the workflow type launched - never actionable, never assigned. Labelled here so it
// reads as a deliberate state rather than the raw enum the `|| wi.status` fallback would show.
const STATUS_LABEL = {
  in_progress: 'In progress',
  completed: 'Completed',
  skipped_backfill: 'Skipped (backfill)',
};

// STATUSPALETTE (Marie-Lynne's decision, pending COO approval): the Financial Workflows status
// palette, scoped to this module only. Replaces the previous gold/dark-green pair (which made
// "in progress" and "completed" both compete visually with the escalated state) with two
// families: navy for completed/in progress (separated by lightness, not hue) and terracotta for
// escalated. Every text/fill pair and border/background pair below is WCAG 2.1 AA verified.
// "ignored" maps to the backend's skipped_backfill status (an audit-only, never-actionable
// record) - the closest existing concept to "ignored" in the instance status vocabulary.
const STATUS_PILL_COLOURS = {
  completed:   { light: { text: '#1A2744', fill: '#FFFFFF', border: '#6A7794' }, dark: { text: '#C6CFDE', fill: '#232C3C', border: '#6B7488' } },
  in_progress: { light: { text: '#12447F', fill: '#D9E7F7', border: '#5A82B4' }, dark: { text: '#9CC2F2', fill: '#0E2749', border: '#4A6EA4' } },
  escalated:   { light: { text: '#8A3427', fill: '#EBDCD8', border: '#976356' }, dark: { text: '#F0A79A', fill: '#2c1310', border: '#9A594E' } },
  ignored:     { light: { text: '#5A626E', fill: '#EDEEF0', border: '#868D97' }, dark: { text: '#8E98A8', fill: '#12161D', border: '#616977' } },
};

// STATUSPALETTE: resolves an instance to one of the 4 palette keys above. Escalation is a flag
// layered onto an in_progress instance (escalated_at set), not a separate backend status value,
// so it is checked first and takes visual priority over the plain "in progress" state - an
// escalated instance is by definition the most urgent one on the screen.
// WFSORT: a genuinely CHRONOLOGICAL key for a reference_period label, NOT an alphabetical one.
// The resolvers emit four different shapes and a plain string sort gets them wrong:
//
//   'YYYY'        annual      (partnership_tax_return, group_liability_insurance)
//   'YYYY-MM'     monthly     (payroll, management_accounts, pension, monthly_travel)
//   'YYYY-Qn'     quarterly   (fca_quarterly_reporting, vat, nppr_filing)
//   'YYYY-MM-DD'  daily       (daily_margin_check)
//
// Alphabetically, 'Q' sorts above every digit, so '2026-Q1' (January to March) would outrank
// '2026-12' (December) - the exact case a month-only list never exposes. This maps each label to
// (year, month, day) so the comparison is real time order across all four shapes.
//
// A quarter is keyed to its FIRST month (Q1 -> 1, Q4 -> 10) and a bare year to month 0, so an
// annual period sorts as the start of its year. Both are deliberate: a period should rank by when
// it BEGINS, which is what makes a quarter sit correctly among the months it contains.
//
// An unparseable or missing label returns -Infinity so it sinks to the bottom rather than
// crashing the sort or jumping to the top - a row with no period is the least useful to surface.
const periodSortKey = (period) => {
  if (!period || typeof period !== 'string') return Number.NEGATIVE_INFINITY;
  const m = period.trim().match(/^(\d{4})(?:-(?:Q([1-4])|(\d{2})(?:-(\d{2}))?))?$/);
  if (!m) return Number.NEGATIVE_INFINITY;
  const year = Number(m[1]);
  const quarter = m[2] ? Number(m[2]) : null;
  const month = quarter ? (quarter - 1) * 3 + 1 : (m[3] ? Number(m[3]) : 0);
  const day = m[4] ? Number(m[4]) : 0;
  return year * 10000 + month * 100 + day;
};

// WFSORT: most recent period first, and WITHIN one period the still-open work sits above the
// finished work. Confirmed reasoning: this list is a worklist before it is an archive, so an
// instance someone still has to act on should never be pushed below one that is closed and
// needs nothing. Ties beyond that fall back to the label, so the order is stable and repeatable
// rather than dependent on whatever order the backend happened to return.
//
// SORTING ONLY: this reorders, it never filters. Every instance the backend returns still
// renders, which is what keeps past periods reachable (the reason this list shows them at all).
const OPEN_FIRST = (wi) => (wi && wi.status === 'completed' ? 1 : 0);

const sortInstances = (list) => [...list].sort((a, b) => {
  const byPeriod = periodSortKey(b.reference_period) - periodSortKey(a.reference_period);
  if (byPeriod !== 0) return byPeriod;
  const byOpen = OPEN_FIRST(a) - OPEN_FIRST(b);
  if (byOpen !== 0) return byOpen;
  return String(a.workflow_label || '').localeCompare(String(b.workflow_label || ''));
});

const statusPillKey = (wi) => {
  if (wi.status === 'in_progress' && wi.escalated_at) return 'escalated';
  if (wi.status === 'completed') return 'completed';
  if (wi.status === 'skipped_backfill') return 'ignored';
  return 'in_progress';
};

// STATUSPALETTE: the pill itself - text label always visible (never a colour-only dot/bar), so
// completed and in-progress (both blue-family, separated by lightness) stay distinguishable for
// colour-blind users per the label, not the hue.
// WFPAUSE: only coo and admin ever receive a paused instance (the backend hides it from every
// other role); it is shown read-only with this pill, in the "ignored" palette.
function PausedPill({ isDarkMode }) {
  const { t } = useTranslation();
  return (
    <span data-testid="wf-paused">
      <StatusPill statusKey="ignored" label={t('workflowPause.paused')} isDarkMode={isDarkMode} />
    </span>
  );
}

function PausedNote() {
  const { t } = useTranslation();
  return (
    <div role="status" data-testid="wf-paused-note" style={{ fontSize: '13px', margin: '6px 0 10px' }}>
      {t('workflowPause.pausedNote')}
    </div>
  );
}

function StatusPill({ statusKey, label, isDarkMode }) {
  const c = STATUS_PILL_COLOURS[statusKey][isDarkMode ? 'dark' : 'light'];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', fontSize: '11px', fontWeight: 600,
      color: c.text, backgroundColor: c.fill, border: `1px solid ${c.border}`,
      borderRadius: '999px', padding: '2px 9px', lineHeight: 1.5,
    }}>
      {label}
    </span>
  );
}

// PAYENTITY: display labels for the per-entity Payroll instances' entity_key (the canonical
// PAYROLL_CONTACTS location keys). An unmapped key renders as-is, so adding a payroll entity
// server-side never makes an instance unlabelled here.
const ENTITY_LABEL = {
  uk: 'UK',
  cameroon: 'Cameroon',
  'ivory coast': 'Ivory Coast',
  'south africa': 'South Africa',
  mauritius: 'Mauritius',
  rwanda: 'Rwanda',
};

// PAYROLLFORM: display labels for every structured form_data key that exists today (Pension's
// NEST confirmation + Payroll's two step forms). Unknown keys fall back to the raw key so a
// future form still renders rather than disappearing.
// PAYROLLROWS: the COO's three change categories are REPEATABLE STRUCTURED ROWS now, not free
// text. These two helpers turn one row into a single readable line so every display site renders
// the same wording, and so nothing ever falls through to String(row) and prints "[object Object]"
// (which is exactly what the generic "Captured form data" card below would otherwise have done).
//
// A row is rendered as "Name - key detail - extra", omitting any part the row does not carry, so
// an optional bonus simply does not appear rather than showing an empty label.
export function formatPayrollRow(key, row) {
  if (!row || typeof row !== 'object') return String(row ?? '');
  const name = row.employee_name || '';
  const parts = [];
  if (key === 'new_joiners') {
    // PAYFIELDS: start date first - it is what tells the reader whether this is a full or
    // part month, which changes how every figure after it is read.
    if (row.start_date) parts.push(`starts ${row.start_date}`);
    if (row.salary) parts.push(`salary ${row.salary}`);
    if (row.bonus) parts.push(`bonus ${row.bonus}`);
    if (row.additional_payment) parts.push(`one-off ${row.additional_payment}`);
  } else if (key === 'leavers') {
    if (row.leaving_date) parts.push(`last working day ${row.leaving_date}`);
    if (row.leaving_payment) parts.push(`leaving payment ${row.leaving_payment}`);
    // ACCRUEDHOL: added to the SHARED formatter, so it reaches the Financial Controller's step
    // and the COO's approval step through the one component both already render - no second
    // display path. Omitted entirely when blank, exactly like leaving payment above.
    if (row.accrued_holiday) parts.push(`accrued holiday ${row.accrued_holiday}`);
  } else if (key === 'salary_changes') {
    if (row.new_salary) parts.push(`new salary ${row.new_salary}`);
    if (row.bonus) parts.push(`comment ${row.bonus}`);
    if (row.document_id) parts.push('document attached');
  } else if (key === 'bonus_premium') {
    // PAYROLLBONUS: the standalone bonus section. Its own row shape (amount), distinct from the
    // `bonus` cell inside a salary_changes row, which is unchanged above.
    // PAYFIELDS: the kind leads, since "Relocation 5000" reads differently from "Bonus 5000".
    if (row.kind) parts.push(row.kind);
    if (row.amount) parts.push(row.amount);
  } else if (key === 'intern_payments') {
    if (row.amount) parts.push(`intern payment ${row.amount}`);
  } else {
    Object.entries(row).forEach(([k, v]) => {
      if (k !== 'employee_name' && k !== 'employee_id' && v) parts.push(`${k} ${v}`);
    });
  }
  return parts.length ? `${name} - ${parts.join(' - ')}` : name;
}

// True for the three payroll change categories, whose values are arrays of rows.
export function isPayrollRowField(key, value) {
  return Array.isArray(value)
    && ['new_joiners', 'leavers', 'salary_changes', 'bonus_premium', 'intern_payments'].includes(key);
}

// PAYFIELDS: the three bonus types, mirroring the server's PAYROLL_BONUS_KINDS exactly. The
// backend validates against its own copy and refuses anything else, so this list only decides
// what the dropdown OFFERS - it is not the enforcement.
export const PAYROLL_BONUS_KINDS = ['Bonus', 'Additional salary', 'Relocation'];

const FORM_FIELD_LABELS = {
  accrued_holiday: 'Accrued holiday',
  submission_date: 'Submission date',
  nest_reference: 'NEST reference',
  new_joiners: 'New joiners',
  leavers: 'Leavers',
  salary_changes: 'Salary changes',
  bonus_premium: 'Bonus',
  intern_payments: 'Intern payments',
  general_comment: 'Comment',
  processing_notes: 'Comment',
  payment_date: 'Payment date',
  comment: 'Comment',
  // LABELDEDUPE: a SECOND `processing_notes: 'Processing notes'` sat here and silently won, since
  // a JS object literal keeps the LAST declaration of a duplicate key. The Financial Controller's
  // comment therefore rendered as "Processing notes:" on a completed instance while the identical
  // field rendered as "Comment:" everywhere else. The duplicate is deleted and line 563's
  // 'Comment' now applies, consistent with `general_comment` and `comment` directly above.
  broker_selected: 'Broker selected',
  premium_amount: 'Premium amount',
  three_offers_received: 'Three offers received',
};

// TESTCREATEGEN: the workflow types available to the QA "Create test instance" control.
// FPREBUILD: fp_assessment was REMOVED from this list along with its registry definition - the
// real F&P process is not a workflow-engine type, so the backend would 404 that key.
// Mirrors WORKFLOW_DEFINITIONS (app/workflows.py) - kept as a literal here because this is a
// QA-only selector and the backend rejects any unknown key with a 404, so a drifted entry fails
// loudly rather than silently creating the wrong thing.
// FCASPLIT: 'fca_quarterly_reporting' was REMOVED from this list. It is retired and the backend
// now refuses to create one (400), so leaving it here - and as the default - would have meant the
// button failing for anyone who just clicked it. The four independent quarterly tasks that
// replaced it lead the list instead, with the compliance solo task as the new default.
const TEST_CREATE_TYPES = [
  ['fca_quarterly_compliance_solo',         'FCA Quarterly - Compliance (Solo)'],
  ['fca_quarterly_compliance_consolidated', 'FCA Quarterly - Compliance (Consolidated)'],
  ['fca_quarterly_finance_solo',            'FCA Quarterly - Finance (Solo)'],
  ['fca_quarterly_finance_consolidated',    'FCA Quarterly - Finance (Consolidated)'],
  ['daily_margin_check',        'Daily Margin Check'],
  ['payroll',                   'Payroll'],
  ['management_accounts',       'Management Accounts'],
  ['partnership_tax_return',    'Partnership Tax Return'],
  ['group_liability_insurance', 'Group Liability Insurance'],
  ['vat',                       'VAT'],
  ['pension',                   'Pension'],
  ['monthly_travel',            'Monthly Travel'],
  ['nppr_filing',               'NPPR Filing'],
];

// Same helper the other QA-only "Fill demo data" controls already use (AddEmployee.js,
// NewStarterForm.js, LeaverForm.js, SponsorForm.js, ComplianceModule.js,
// ApplicationRequestForm.js) - reads the is_test_account flag the backend returns at login
// (auth.py: account.email == settings.TEST_COO_EMAIL) and App.js stores in enko_auth.
const isTestAccount = () => { try { return JSON.parse(localStorage.getItem('enko_auth'))?.is_test_account === true; } catch { return false; } };

// Generic list + detail view for GET /workflows/ and GET /workflows/{id} - the visibility rules
// (COO sees all, Financial Controller sees all Finance-category, everyone else sees only
// instances they're directly involved in) are enforced entirely server-side, so this component
// simply renders whatever the API returns for the logged-in viewer. Reused unchanged across
// FinanceView, ComplianceView and Dashboard.js (COO) - one component, one API, three entry points.
//
// currentUserRole (optional): when 'coo' or 'admin' AND the viewer is on the designated test
// account (is_test_account, see isTestAccount above), shows a small QA-only "Create test
// instance" button that POSTs to /workflows/fca-quarterly/test-create. A real production COO no
// longer sees it - previously any coo/admin did, which put a QA control in front of live users.
// The backend stays gated to the same 2 roles regardless of this prop: this is a UI convenience,
// not the access control.
// currentUserRole is now also passed by FinanceView/ComplianceView (previously omitted, since
// only the test-create button needed it) - the interactive step-completion action panel in the
// detail view below needs to know the viewer's role too, to show "Mark complete"/upload only to
// whoever owns the current step.
//
// COOWORKFLOWSPLIT: scope (optional) narrows GET /workflows/?scope=... server-side for the
// COO's 3-way sidebar split (MAIN "Workflows" = 'involved', FINANCE "Financial workflows" =
// 'finance', COMPLIANCE "Compliance workflows" = 'compliance'). Every other entry point
// (FinanceView, ComplianceView, and any coo/admin route that omits scope) is unaffected - the
// backend only reads this parameter for the coo/admin role, and defaults to the full permitted
// set when scope is omitted. title (optional) overrides the "Workflows" heading below it, so
// each of the 3 COO entry points can show its own label without a separate component.
export default function WorkflowList({ onBack, currentUserRole, scope, title }) {
  const { isDarkMode } = useDarkMode();
  const { t } = useTranslation();
  // WFCONCERN: skipped_backfill instances (handled in the previous system) are hidden by default,
  // for every role, until "Show older instances" is ticked.
  const [showOlder, setShowOlder] = useState(false);
  // A11YLABEL: one id prefix per component INSTANCE, so two copies of this
  // form on one page cannot produce duplicate ids.
  const a11yId = useId();
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // ASSIGNEECHECK: this component's own identity, via the same GET /employees/me pattern every
  // other shell already uses for "who am I as an employee" (App.js, AddEmployee.js) - not a new
  // KIND of call, just the one this component happened not to make before. Needed to compare
  // against current_assignee_id, since the owner_role check alone does not tell the viewer
  // whether THEY are the person a step is actually assigned to.
  const [myEmployeeId, setMyEmployeeId] = useState(null);
  useEffect(() => {
    axios.get(`${API}/employees/me`)
      .then(res => setMyEmployeeId(res.data?.id || null))
      .catch(() => setMyEmployeeId(null));
  }, []);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [testCreateLoading, setTestCreateLoading] = useState(false);
  const [testCreateMsg, setTestCreateMsg] = useState(null);
  // TESTCREATEGEN: which workflow type the QA control will create.
  // FCASPLIT: the default moved off the retired 'fca_quarterly_reporting', which the backend now
  // refuses, onto the first of the four types that replaced it.
  const [testCreateType, setTestCreateType] = useState('fca_quarterly_compliance_solo');
  // Interactive step-completion action state - additive to the existing read-only detail view,
  // not a change to it. actionFile/actionError/actionLoading are reset on every openInstance()
  // call so switching between instances never leaks a stale error/file selection across them.
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [actionFile, setActionFile] = useState(null);
  // PENSIONCOMMENT: the optional comment typed alongside an upload-completion step (today, the
  // Financial Controller's "Submit pension contributions"). Reset on every openInstance() like
  // actionFile, so a comment typed on one instance never leaks into another.
  const [actionComment, setActionComment] = useState('');
  // PENSIONFORM: the two structured fields for Pension's "Confirm NEST submission" step.
  // Reset on every openInstance() like actionFile, so a value typed on one instance never
  // leaks into another.
  const [formDate, setFormDate] = useState('');
  const [formRef, setFormRef] = useState('');
  // PAYROLLFORM: Payroll's two step forms - the COO's monthly changes confirmation
  // (no-changes checkbox + three optional text areas) and the FC's processing notes.
  // Reset in openInstance() for the same reason as the Pension fields.
  const [formNoChanges, setFormNoChanges] = useState(false);
  // PAYROLLPICK: New joiners / Leavers are now selected from real employees, held as
  // [{id, name}]; Salary changes stays free text (formSalary).
  // PAYROLLROWS: the three categories are lists of ROWS now, not two pickers plus a textarea.
  // Each row carries its own cells; rows are added and removed independently per category.
  const [formJoiners, setFormJoiners] = useState([]);
  const [formLeavers, setFormLeavers] = useState([]);
  const [formSalaryRows, setFormSalaryRows] = useState([]);
  // PAYROLLBONUS: the fourth section's rows - a standalone bonus or premium, paid
  // outside any salary change. Its own state, independent of the bonus cell inside a
  // salary-change row.
  const [formBonusRows, setFormBonusRows] = useState([]);
  // PAYFIELDS: the fifth category and the form's own comment box.
  const [formInternRows, setFormInternRows] = useState([]);
  const [formGeneralComment, setFormGeneralComment] = useState('');
  // PAYFIELDS: the Financial Controller's own step 2 fields - her comment and the
  // payroll provider's reports as an attachment. Both optional.
  const [formProcessingNotes, setFormProcessingNotes] = useState('');
  const [formProcessingDoc, setFormProcessingDoc] = useState(null);
  // GLIBRANCH: Group Liability's gateway (three offers received yes/no) and broker-selection form.
  const [formBroker, setFormBroker] = useState('');
  const [formPremium, setFormPremium] = useState('');
  // WFREJECT: the mandatory reason for sending an approval-gate step back, and whether the
  // reason box is open. Separate from the form state above because rejection is not a step form -
  // it is an alternative OUTCOME of an approval step, available alongside its completion control.
  // PAYROLLFINALAPPROVE: the COO's step 3 payment confirmation (date + bank reference). Held
  // separately from formDate/formRef, which belong to Pension's NEST form - sharing them would
  // couple two unrelated steps' state and leak one step's entry into the other's panel.
  const [formPayDate, setFormPayDate] = useState('');
  // PAYFIELDS: the payment reference was REMOVED - confirmed on the call that it serves no
  // purpose. An optional comment replaces it; the date stays required.
  const [formPayComment, setFormPayComment] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  // PAYROLLPICK: active employees for the pickers, loaded when the Payroll confirm step is
  // opened. GET /employees/ is coo/admin/hr/compliance-only; PAYROLLREVERSE made that step the
  // COO's, so its viewer IS in that set and the list normally loads - the 403 fallback below is
  // kept anyway rather than removed, since it costs nothing and an empty list degrades safely to
  // "manual-add search finds no matches". The pre-populated joiners/leavers come from the
  // dedicated payroll-prepopulate endpoint, which is gated on instance visibility, not role.
  const [activeEmployees, setActiveEmployees] = useState([]);
  // PAYROLLAUTO: the pre-populated month summary (joiners/leavers/bank_changes) for step 1, and
  // whether it has been loaded, so the panel can show "loading" vs an empty "no changes" state.
  const [payrollPrefill, setPayrollPrefill] = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    axios.get(`${API}/workflows/`, { params: scope ? { scope } : {} })
      .then(res => { setInstances(res.data.workflow_instances || []); setLoading(false); })
      .catch(() => { setError('Could not load workflows.'); setLoading(false); });
  };

  // COOWORKFLOWSPLIT: re-fetch if scope changes on an already-mounted instance (e.g. a future
  // caller that toggles scope via state rather than mounting a fresh component per nav item, as
  // Dashboard.js/App.js do today via activeSection remounts).
  useEffect(() => { load(); }, [scope]);

  // TESTCREATEGEN: FCA Quarterly keeps its own dedicated endpoint (untouched); every other type
  // goes through the generalised one. The URL is the only difference, so one handler serves both.
  const handleTestCreate = () => {
    setTestCreateLoading(true);
    setTestCreateMsg(null);
    const url = testCreateType === 'fca_quarterly_reporting'
      ? `${API}/workflows/fca-quarterly/test-create`
      : `${API}/workflows/${testCreateType}/test-create`;
    axios.post(url)
      .then(res => {
        setTestCreateLoading(false);
        setTestCreateMsg(res.data.already_existed
          ? 'This period already has an instance - shown below.'
          : 'Test instance created.');
        load();
      })
      .catch(err => {
        setTestCreateLoading(false);
        setTestCreateMsg(err.response?.data?.detail || 'Could not create test instance.');
      });
  };

  // EMAILBTN: an email button about one workflow arrives with ?instance=<id>: open it directly.
  // Read once, from the address bar, so this works under every shell that mounts this list.
  const openedFromLink = useRef(false);
  useEffect(() => {
    if (openedFromLink.current) return;
    openedFromLink.current = true;
    let iid = null;
    try { iid = new URLSearchParams(window.location.search).get('instance'); } catch { iid = null; }
    if (iid && /^[A-Za-z0-9_-]{1,64}$/.test(iid)) openInstance(iid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openInstance = (id) => {
    setDetailLoading(true);
    setActionError(null);
    setActionFile(null);
    setActionComment('');   // PENSIONCOMMENT: cleared with actionFile, for the same reason.
    setFormDate('');
    setFormRef('');
    setFormNoChanges(false);
    setFormJoiners([]);
    setFormLeavers([]);
    setFormSalaryRows([]);
    setFormBonusRows([]);
    setFormInternRows([]);
    setFormGeneralComment('');
    setFormProcessingNotes('');
    setFormProcessingDoc(null);
    setFormBroker('');
    setFormPremium('');
    setFormPayDate('');
    setFormPayComment('');
    setRejectReason('');
    setRejectOpen(false);
    setPayrollPrefill(null);
    axios.get(`${API}/workflows/${id}`)
      .then(res => {
        setSelected(res.data);
        setDetailLoading(false);
        const step = res.data.steps?.find(s => s.key === res.data.current_step);
        // PAYENTITY: Payroll is one instance per entity now, each owned by that entity's
        // payroll contact - Gaynor's finance account for the UK instance, a payroll-role
        // account for each of the others. Visibility is contact-scoped server-side, so a
        // finance/payroll viewer who can open a payroll instance IS its contact and gets the
        // editable confirm form (a coo/admin test-account viewer fails the role check below and
        // stays read-only). PAYSPLIT's separate read-only entity panel is gone - the contact
        // now owns the step, and the prepopulate lists arrive filtered to the instance's entity.
        // PAYROLLREVERSE: confirm_payroll_changes is the COO's step now (he sends the month's
        // changes), so the editable form + pre-population load for the COO. admin is included
        // for the same reason it is exempt from the assignee check elsewhere in this file -
        // coo/admin are the two roles the backend treats as interchangeable on the generic path.
        const ownsConfirm = res.data.workflow_type === 'payroll'
          && step?.key === 'confirm_payroll_changes'
          && (currentUserRole === 'coo' || currentUserRole === 'admin');
        if (ownsConfirm) {
          // PAYROLLPICK: employees for manual add/remove in the pickers (finance/payroll may
          // 403 on the full list - non-fatal, manual search just returns nothing then).
          // PAYROLLROWS: the name search is ENTITY-SCOPED now. GET /employees/?status=active
          // returns the whole directory (and full employee records at that), which would offer
          // the COO another entity's staff for this entity's payroll run. The dedicated
          // payroll-employee-search endpoint applies the instance's own entity resolution and
          // returns names only. Refetched per instance, since the scope differs per entity.
          axios.get(`${API}/workflows/${id}/payroll-employee-search`)
            .then(r => setActiveEmployees(
              (r.data || []).map(e => ({ id: e.id, name: `${e.first_name} ${e.last_name}` }))))
            .catch(() => setActiveEmployees([]));
          // PAYROLLAUTO: pull the month's captured changes and PRE-FILL the editable fields.
          axios.get(`${API}/workflows/${id}/payroll-prepopulate`)
            .then(r => {
              const pf = r.data || { joiners: [], leavers: [], bank_changes: [] };
              setPayrollPrefill(pf);
              // PAYROLLROWS: the month's captured joiners/leavers seed one ROW each, with the
              // name already filled in - the COO then adds the salary/bonus (or the leaving date)
              // that the portal cannot know. Same pre-population as before, shaped as rows.
              setFormJoiners((pf.joiners || []).map(x => ({
                employee_id: x.id, employee_name: x.name, salary: '', bonus: '',
              })));
              setFormLeavers((pf.leavers || []).map(x => ({
                employee_id: x.id, employee_name: x.name, leaving_date: '',
              })));
            })
            .catch(() => setPayrollPrefill({ joiners: [], leavers: [], bank_changes: [], _error: true }));
        }
        // PAYFIELDS: the Financial Controller's own step 2 needs the SAME pre-population, but
        // only for its bank_changes - she asked to see the period's bank account changes on her
        // screen automatically rather than searching for them. Reuses the identical endpoint and
        // the identical state, so there is one source for that list and no second mechanism.
        // She does NOT get the joiner/leaver row seeding above: those fields are the COO's.
        const ownsProcessing = res.data.workflow_type === 'payroll'
          && step?.key === 'process_payroll';
        if (ownsProcessing) {
          axios.get(`${API}/workflows/${id}/payroll-prepopulate`)
            .then(r => setPayrollPrefill(r.data || { joiners: [], leavers: [], bank_changes: [] }))
            .catch(() => setPayrollPrefill({ joiners: [], leavers: [], bank_changes: [], _error: true }));
        }
      })
      .catch(() => { setError('Could not load this workflow.'); setDetailLoading(false); });
  };

  // Current step's action, if any is available to the logged-in viewer. Exact role match only
  // (no coo/admin equivalence widening) - mirrors the backend's own literal
  // `current_user.role != step.owner_role` gate exactly, per this task's "only the step's owner
  // role" instruction.
  const shownInstances = showOlder ? instances : instances.filter(wi => wi.status !== 'skipped_backfill');
  const currentStep = selected?.steps?.find(s => s.key === selected.current_step);
  // PAYENTITY: a per-entity Payroll instance's finance-owned confirm step is completed by that
  // entity's payroll contact, who may hold the finance role (Gaynor, UK) or the payroll role
  // (the 5 country contacts) - so the literal role==owner_role match is widened for exactly
  // that case. The backend enforces the real rule (contact email vs PAYROLL_CONTACTS, a DIFFERENT
  // check from current_assignee_id below) - this only decides whether the panel is offered. Every
  // other workflow type keeps the strict match. Deliberately NOT touched by ASSIGNEECHECK: this
  // branch's real gate is the payroll-contact resolution, not current_assignee_id, so adding that
  // comparison here would check the wrong thing.
  const isEntityPayrollStep = Boolean(
    selected?.workflow_type === 'payroll' && selected?.entity_key
    && currentStep?.owner_role === 'finance'
  );
  // ASSIGNEECHECK: mirrors the backend's SECOND gate (workflows.py, complete-step), not just the
  // first. The backend's finance exemption depends on FINANCE_ASSIGNEE_STRICT, a server-only
  // settings flag never exposed to the frontend and set once at deploy time - mirroring it here
  // would mean guessing its current value or adding a call purely to read it, which this fix does
  // not do. Instead coo/admin (always exempt server-side, in every state of that flag) are the
  // only ones treated as exempt here; everyone else, including finance, must match
  // current_assignee_id. This is DELIBERATELY the conservative direction: if finance is actually
  // exempt server-side today (the flag is false), a finance viewer who is not the literal
  // assignee simply will not see the button here, even though calling the endpoint directly would
  // still succeed for them - a HIDDEN capability, not a shown-but-refused one, which is the safe
  // side of this exact defect class. myEmployeeId starting null fails this check safely (no flash
  // of a control the viewer cannot yet be confirmed to hold).
  const isAssigneeExempt = currentUserRole === 'coo' || currentUserRole === 'admin';
  const isAssignedToMe = Boolean(
    myEmployeeId && selected?.current_assignee_id === myEmployeeId
  );
  // WFREJECT: mirrors the backend's `_rejectable_step` - the two workflow types that have a real
  // approval in their process, and is_approval_gate read from the serialized step rather than a
  // hard-coded step key, so this stays correct if a gate ever moves. Payroll is deliberately
  // absent: it has no approval gate (see WFREJECT_TYPES in workflows.py).
  const isRejectableStep = Boolean(
    currentStep?.is_approval_gate
    && (selected?.workflow_type === 'management_accounts'
      || selected?.workflow_type === 'partnership_tax_return')
  );
  const canAct = Boolean(
    // WFPAUSE: no step action on a paused instance (the backend refuses it too, 409).
    selected && selected.status === 'in_progress' && currentStep && !selected.paused
    && (isEntityPayrollStep
      ? (currentUserRole === 'finance' || currentUserRole === 'payroll')
      : currentUserRole === currentStep.owner_role && (isAssigneeExempt || isAssignedToMe))
  );

  // Re-fetches both the open instance (so the diagram/action panel reflect the new current step)
  // and the list (so its assignee/status summary is current when the viewer navigates back) -
  // refetch-after-action, matching this codebase's existing pattern elsewhere (e.g.
  // LeaveDeclarationForm.js's decide() reloading pending approvals) rather than hand-patching
  // local state to guess the server's post-action shape.
  // WFREJECT: send an approval-gate step back with a mandatory reason. The backend refuses an
  // empty reason too (400) - this check only avoids a pointless round trip and gives the message
  // immediately; it is NOT the enforcement, which stays server-side.
  const handleRejectStep = () => {
    const reason = rejectReason.trim();
    if (!reason) {
      setActionError('A reason is required to send this step back.');
      return;
    }
    setActionLoading(true);
    setActionError(null);
    axios.post(`${API}/workflows/${selected.id}/reject-step`, { reason })
      .then(() => {
        setActionLoading(false);
        setRejectReason('');
        setRejectOpen(false);
        openInstance(selected.id);
        load();
      })
      .catch(err => {
        setActionLoading(false);
        const detail = err.response?.data?.detail;
        setActionError(typeof detail === 'string' ? detail : 'Could not send this step back.');
      });
  };

  const handleCompleteStep = () => {
    setActionLoading(true);
    setActionError(null);
    axios.post(`${API}/workflows/${selected.id}/complete-step`)
      .then(() => { setActionLoading(false); openInstance(selected.id); load(); })
      .catch(err => { setActionLoading(false); setActionError(err.response?.data?.detail || 'Could not complete this step.'); });
  };

  // PENSIONFORM/PAYROLLFORM: shared POST to the form completion endpoint, same
  // refetch-after-action pattern as the other handlers. The backend's 422 detail is a list of
  // pydantic error objects, not a string - guard so a non-string detail never reaches render.
  const postStepForm = (payload) => {
    setActionLoading(true);
    setActionError(null);
    axios.post(`${API}/workflows/${selected.id}/complete-step/form`, payload)
      .then(() => { setActionLoading(false); openInstance(selected.id); load(); })
      .catch(err => {
        setActionLoading(false);
        const d = err.response?.data?.detail;
        setActionError(typeof d === 'string' ? d : 'Could not complete this step - check the form values.');
      });
  };

  // Client-side required checks mirror the backend's own validation (which remains the real gate).
  const handleFormComplete = () => {
    if (!formDate || !formRef.trim()) { setActionError('Enter the submission date and the NEST reference.'); return; }
    postStepForm({ submission_date: formDate, nest_reference: formRef.trim() });
  };

  // GLIBRANCH: gateway answer routes the branch server-side via {outcome} in the cleaner.
  const handleGliBids = (received) => postStepForm({ received });
  const handleGliBroker = () => {
    if (!formBroker.trim() || !formPremium.trim()) {
      setActionError('Enter the selected broker and the premium amount.'); return;
    }
    postStepForm({ broker_selected: formBroker.trim(), premium_amount: formPremium.trim() });
  };

  // PAYROLLROWS: a row the COO added but never filled in is dropped here rather than sent, so an
  // untouched trailing row (the UI adds an empty one on "Add") is never a validation error he has
  // to clear. The backend drops them identically - this is a convenience, not the enforcement.
  const nonEmptyRows = (rows, cells) =>
    rows.filter(r => cells.some(c => String(r[c] || '').trim()));

  // PAYFIELDS: uploads a row's supporting document through the portal's ONE existing endpoint
  // and stores only the returned document_id on the row - the same mechanism every other F&P and
  // workflow attachment uses. A failed upload surfaces as an error rather than silently
  // submitting a row that claims an attachment it does not have.
  const onRowFile = async (file, patch, row) => {
    if (!file) { patch({ document_id: null }); return; }
    // PAYSCREENFIX: this sent `selected.id` - the WORKFLOW INSTANCE id - as employee_id. No
    // Employee row has that id, so POST /documents/upload raised 404 "Employee not found" and the
    // screen showed "Could not upload that document." every time. The document belongs to the
    // person the ROW is about, so the row's own employee_id is what goes.
    //
    // That id is set only when the name was PICKED from the entity-scoped search; a typed free
    // text name has none, and there is no correct record to file against. Rather than guess (the
    // COO's own record would be wrong, and silently so), the control is disabled in that case and
    // says why - see the file input below. This guard is the server-side-truth backstop for it.
    const employeeId = row?.employee_id || null;
    if (!employeeId) {
      setActionError('Pick the employee from the search before attaching a document.');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('employee_id', employeeId);
      fd.append('document_type', 'other');
      // PAYFOLDER: route this to Finance/Payroll/{period}/Salary Changes on the Finance site,
      // instead of the flat Other/ folder on the HR site where it landed among every unrelated
      // document. The period comes from the instance itself, so the file files itself by the
      // payroll run it belongs to rather than by whoever happened to be named on the row.
      fd.append('payroll_period', selected?.reference_period || '');
      fd.append('payroll_kind', 'salary_change');
      fd.append('file', file);
      const res = await axios.post(`${API}/documents/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      patch({ document_id: res.data?.document_id || null });
    } catch (err) {
      // UPLOADREFUSE: the server's real reason.
      setActionError(refusalMessage(err, currentLang(), 'uploadChecks.uploadFailed'));
    }
  };

  // PAYFIELDS: step 2 is a form step now. Both cells are optional, so she is never blocked from
  // confirming a month with nothing to attach - the backend applies the identical rule.
  const handlePayrollProcessing = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      let documentId = null;
      if (formProcessingDoc) {
        const fd = new FormData();
        fd.append('employee_id', selected?.current_assignee_id || '');
        fd.append('document_type', 'other');
        // PAYFOLDER: Finance/Payroll/{period}/Provider Reports, same site and same period as the
        // salary change documents above, so one month's payroll paperwork sits together.
        fd.append('payroll_period', selected?.reference_period || '');
        fd.append('payroll_kind', 'provider_report');
        fd.append('file', formProcessingDoc);
        const up = await axios.post(`${API}/documents/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        documentId = up.data?.document_id || null;
      }
      await axios.post(`${API}/workflows/${selected.id}/complete-step/form`, {
        processing_notes: formProcessingNotes.trim() || null,
        document_id: documentId,
      });
      setActionLoading(false);
      openInstance(selected.id);
      load();
    } catch (err) {
      setActionLoading(false);
      const d = err.response?.data?.detail;
      setActionError(typeof d === 'string' ? d : 'Could not complete this step.');
    }
  };

  const handlePayrollChanges = () => {
    const joiners = nonEmptyRows(formJoiners, ['employee_name', 'salary', 'bonus']);
    const leavers = nonEmptyRows(formLeavers, ['employee_name', 'leaving_date']);
    const salaries = nonEmptyRows(formSalaryRows, ['employee_name', 'new_salary', 'bonus']);
    const bonuses = nonEmptyRows(formBonusRows, ['employee_name', 'amount']);
    const interns = nonEmptyRows(formInternRows, ['employee_name', 'amount']);

    if (!formNoChanges && !joiners.length && !leavers.length && !salaries.length
        && !bonuses.length && !interns.length) {
      setActionError("Add at least one change row, or tick 'No changes this month'.");
      return;
    }
    if (formNoChanges && (joiners.length || leavers.length || salaries.length
        || bonuses.length || interns.length)) {
      setActionError("'No changes this month' is ticked but change rows were entered - untick it or remove the rows.");
      return;
    }
    postStepForm(formNoChanges
      ? { no_changes: true }
      : {
          no_changes: false,
          new_joiners: joiners.map(r => ({
            employee_id: r.employee_id || null,
            employee_name: String(r.employee_name || '').trim(),
            salary: String(r.salary || '').trim(),
            // PAYSCREENFIX: `bonus` is no longer sent for a new joiner. Removing only the input
            // would have left this building an empty string into every payload - a field the form
            // no longer asks about. The backend model keeps `bonus` OPTIONAL, so omitting it is
            // accepted and the separate Bonus category remains the one place a bonus is recorded.
            // PAYFIELDS: required by the backend - a row without it is refused there.
            start_date: r.start_date || null,
            additional_payment: String(r.additional_payment || '').trim(),
          })),
          leavers: leavers.map(r => ({
            employee_id: r.employee_id || null,
            employee_name: String(r.employee_name || '').trim(),
            leaving_date: r.leaving_date,
            leaving_payment: String(r.leaving_payment || '').trim(),
            // ACCRUEDHOL: sent as typed. The backend omits it from the stored entry when blank.
            accrued_holiday: String(r.accrued_holiday || '').trim(),
          })),
          salary_changes: salaries.map(r => ({
            employee_id: r.employee_id || null,
            employee_name: String(r.employee_name || '').trim(),
            new_salary: String(r.new_salary || '').trim(),
            bonus: String(r.bonus || '').trim(),
            document_id: r.document_id || null,
          })),
          bonus_premium: bonuses.map(r => ({
            employee_id: r.employee_id || null,
            employee_name: String(r.employee_name || '').trim(),
            amount: String(r.amount || '').trim(),
            kind: r.kind || null,
          })),
          intern_payments: interns.map(r => ({
            employee_id: r.employee_id || null,
            employee_name: String(r.employee_name || '').trim(),
            amount: String(r.amount || '').trim(),
          })),
          general_comment: formGeneralComment.trim() || null,
        });
  };

  // PAYROLLFINALAPPROVE: the COO confirms the payroll payments have been made. Both fields are
  // required by the backend too (422/400) - these checks only avoid a pointless round trip and
  // give the message immediately; they are NOT the enforcement.
  const handlePayrollPaymentApproval = () => {
    if (!formPayDate) {
      setActionError('Enter the payment date.');
      return;
    }
    postStepForm({ payment_date: formPayDate, comment: formPayComment.trim() || null });
  };

  const handleUploadAndComplete = () => {
    // The FILE REMAINS MANDATORY: this guard is untouched, and the comment below is appended
    // only after it has passed, so a comment can never stand in for the upload.
    if (!actionFile) { setActionError('Choose a file first.'); return; }
    setActionLoading(true);
    setActionError(null);
    const fd = new FormData();
    fd.append('file', actionFile);
    // PENSIONCOMMENT: appended ONLY when something was actually typed, so an empty box sends no
    // field at all and the request is byte-identical to what it was before this existed.
    if (actionComment.trim()) fd.append('comment', actionComment.trim());
    axios.post(`${API}/workflows/${selected.id}/complete-step/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(() => { setActionLoading(false); openInstance(selected.id); load(); })
      .catch(err => { setActionLoading(false); setActionError(err.response?.data?.detail || 'Could not upload and complete this step.'); });
  };

  const textMuted = isDarkMode ? 'rgba(255,255,255,0.5)' : '#888';
  const cardBg = isDarkMode ? 'rgba(255,255,255,0.03)' : '#ffffff';
  const border = isDarkMode ? 'rgba(255,255,255,0.08)' : '#eef0f2';

  if (selected) {
    return (
      <div>
        <button
          onClick={() => setSelected(null)}
          style={{ background: 'none', border: 'none', color: '#c8a84b', fontSize: '13px', cursor: 'pointer', marginBottom: '16px', padding: 0 }}
        >
          ← Back to workflows
        </button>
        <h2 style={{ margin: '0 0 4px', fontSize: '18px' }}>
          {selected.workflow_label}
          {selected.entity_key ? ` (${ENTITY_LABEL[selected.entity_key] || selected.entity_key})` : ''}
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', fontSize: '13px', color: textMuted, marginBottom: '4px' }}>
          {selected.reference_period && <span>{selected.reference_period}</span>}
          {/* STATUSPALETTE: the instance status pill - text, fill and border together, never a
              colour-only indicator. escalated_at (when set) shows its own date alongside. */}
          <StatusPill
            statusKey={statusPillKey(selected)}
            label={statusPillKey(selected) === 'escalated' ? 'Escalated' : (STATUS_LABEL[selected.status] || selected.status)}
            isDarkMode={isDarkMode}
          />
          {selected.paused && <PausedPill isDarkMode={isDarkMode} />}
          {selected.due_date && <span>Due {toDisplayDate(selected.due_date).toLocaleDateString()}</span>}
          {selected.escalated_at && <span>Escalated {toDisplayDate(selected.escalated_at).toLocaleDateString()}</span>}
        </div>
        {selected.paused && <PausedNote />}
        {selected.current_assignee_name && (
          <div style={{ fontSize: '13px', color: textMuted, marginBottom: '16px' }}>
            Currently with: {selected.current_assignee_name}
          </div>
        )}
        <div style={{ background: cardBg, border: `1px solid ${border}`, borderRadius: '10px', marginTop: '12px' }}>
          {/* STEPEVIDENCE: the diagram delegates evidence rendering back here, so its own file
              stays layout-only while the link becomes a token-scoped control. Generic over
              step.key, so all six requires_upload steps get it, not just pension. */}
          <WorkflowDiagram
            steps={selected.steps}
            renderEvidence={(step) => (
              <span style={{ fontSize: '12px' }}>
                Evidence
                <StepEvidenceLink instanceId={selected.id} stepKey={step.key} />
              </span>
            )}
          />
        </div>
        {/* PAYROLLFORM: full captured form data per completed step (the diagram only has room
            for a compact summary line). Generic over form_data keys via FORM_FIELD_LABELS, so
            Pension's and Payroll's records render through the same block. */}
        {/* SUMMARYATTACH: a step with EVIDENCE but no form_data (the five non-payroll
            requires_upload steps, and pension when no comment was typed) was excluded from this
            card entirely, so its attachment had nowhere to appear. The card now includes any
            completed step carrying EITHER form_data or evidence. */}
        {(selected.steps || []).some(s => s.status === 'done' && (s.form_data || s.evidence_url)) && (
          <div style={{ background: cardBg, border: `1px solid ${border}`, borderRadius: '10px', marginTop: '12px', padding: '16px 18px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px' }}>Captured form data</div>
            {/* CARDPOLISH: presentation only - the data shown and every label/value string are
                byte-identical to before. The card read as one dense block: rows sat 2px apart and
                the categories had no separation or containment of their own. Each structured
                category is now a contained block (subtle background, border, radius) with its
                rows spaced to be scannable, and consecutive completed steps are separated by a
                divider. Non-structured values (Pension's date/reference, GLI's broker/premium)
                keep their exact inline rendering - only their spacing is eased. */}
            {(selected.steps || []).filter(s => s.status === 'done' && (s.form_data || s.evidence_url)).map((s, si) => (
              <div
                key={s.key}
                style={{
                  marginBottom: '14px',
                  paddingTop: si === 0 ? 0 : '14px',
                  borderTop: si === 0 ? 'none' : `1px solid ${border}`,
                }}
              >
                <div style={{ fontSize: '12.5px', fontWeight: 600, marginBottom: '8px' }}>
                  {s.label}
                  <span style={{ color: textMuted, fontWeight: 400 }}>
                    {s.actor_name ? ` · ${s.actor_name}` : ''}
                    {s.completed_at ? ` · ${toDisplayDate(s.completed_at).toLocaleDateString()}` : ''}
                  </span>
                </div>
                {Object.entries(s.form_data || {}).map(([k, v]) => (
                  k === 'no_changes'
                    ? (v === true
                        ? <div key={k} style={{ fontSize: '12.5px', color: textMuted, marginTop: '6px' }}>No changes this month</div>
                        : null)
                    : (
                      isPayrollRowField(k, v)
                        ? (
                          /* PAYROLLROWS: a structured category renders one readable line per row.
                             Without this branch String(v) would print "[object Object]" for every
                             row, which is what this card did before the rows existed.
                             CARDPOLISH: each category is its own contained block so the three
                             read as distinct groups rather than running together. */
                          <div
                            key={k}
                            style={{
                              fontSize: '12.5px',
                              marginTop: '8px',
                              padding: '8px 10px',
                              borderRadius: '8px',
                              border: `1px solid ${border}`,
                              background: isDarkMode ? 'rgba(255,255,255,0.03)' : '#fafbfc',
                            }}
                          >
                            <div style={{ color: textMuted, fontWeight: 600, marginBottom: '4px' }}>
                              {FORM_FIELD_LABELS[k] || k}:
                            </div>
                            {v.map((row, i) => (
                              <div key={i} style={{ marginLeft: '10px', marginTop: i === 0 ? 0 : '5px' }}>
                                {formatPayrollRow(k, row)}
                                {/* SUMMARYATTACH: the row's attachment, rendered BESIDE the
                                    formatted line rather than inside it. formatPayrollRow still
                                    returns a STRING and is not touched - other call sites depend
                                    on that contract - so the control is appended here, where JSX
                                    is allowed. Before this, a completed instance showed only the
                                    formatter's dead words "document attached". */}
                                {row && row.document_id ? (
                                  <PayrollAttachmentLink
                                    instanceId={selected.id}
                                    documentId={row.document_id}
                                  />
                                ) : null}
                              </div>
                            ))}
                          </div>
                        )
                        : k === 'document_id'
                          ? (
                            /* SUMMARYATTACH: a document id is an OPAQUE REFERENCE, not a value to
                               print. String(v) rendered the raw UUID, which told the reader
                               nothing and offered no way to open the file. It now renders through
                               the same scoped route every other payroll attachment uses. */
                            <div key={k} style={{ fontSize: '12.5px', marginTop: '6px' }}>
                              <span style={{ color: textMuted }}>Attachment: </span>
                              <PayrollAttachmentLink instanceId={selected.id} documentId={v} />
                            </div>
                          )
                          : (
                            <div key={k} style={{ fontSize: '12.5px', marginTop: '6px' }}>
                              <span style={{ color: textMuted }}>{FORM_FIELD_LABELS[k] || k}: </span>
                              <span style={{ whiteSpace: 'pre-wrap' }}>{String(v)}</span>
                            </div>
                          )
                    )
                ))}
                {/* SUMMARYATTACH: the step's uploaded evidence, through the scoped step-evidence
                    route rather than the diagram's tokenless raw SharePoint link. Generic over
                    step.key, so all six requires_upload steps are covered by this one block. */}
                {s.evidence_url ? (
                  <div style={{ fontSize: '12.5px', marginTop: '6px' }}>
                    <span style={{ color: textMuted }}>Evidence: </span>
                    <StepEvidenceLink instanceId={selected.id} stepKey={s.key} />
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
        {/* Interactive step-completion action - additive panel below the existing read-only
            diagram, shown only when the logged-in viewer's role matches the current step's
            owner_role (per WorkflowStep.owner_role in app/workflows.py) and the instance isn't
            already completed. */}
        {canAct && (
          <div style={{ background: cardBg, border: `1px solid ${border}`, borderRadius: '10px', marginTop: '12px', padding: '16px 18px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px' }}>
              Your action: {currentStep.label}
            </div>
            {actionError && <div style={{ fontSize: '12px', color: '#A63A3A', marginBottom: '10px' }}>{actionError}</div>}
            {currentStep.requires_form ? (
              /* PENSIONFORM/PAYROLLFORM: structured form for a requires_form step, branched by
                 step key (mirrors the backend's _STEP_FORMS registry). Checked before
                 requires_upload so a step could never render both panels; the backend refuses
                 the other two endpoints for form steps regardless. */
              currentStep.key === 'confirm_payroll_changes' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {/* PAYROLLAUTO, now the COO's step (PAYROLLREVERSE): what the portal captured
                      this month. Joiners/leavers are pre-filled into the editable pickers below;
                      bank changes are shown as a read-only heads-up (they reach the Financial
                      Controller through the separate BANKCHANGE alert and the provider, not
                      through this form). The COO reviews, edits the pickers, and sends. */}
                  {payrollPrefill === null ? (
                    <div style={{ fontSize: '12px', color: textMuted }}>Loading this month's captured changes…</div>
                  ) : (
                    <div style={{ fontSize: '12px', color: textMuted }}>
                      Pre-filled from this month ({selected.reference_period}):{' '}
                      {(payrollPrefill.joiners || []).length} new joiner(s),{' '}
                      {(payrollPrefill.leavers || []).length} leaver(s),{' '}
                      {(payrollPrefill.bank_changes || []).length} bank change(s). Review and edit below.
                      {payrollPrefill._error && ' (could not load pre-population - enter manually)'}
                    </div>
                  )}
                  {/* PAYSCREENFIX (confirmed by Marie-Lynne from tonight's screenshots): the bank
                      account change summary has MOVED to the Financial Controller's "Process
                      payroll with provider" step, where the person who acts on a changed bank
                      detail actually works. It is deliberately gone from the COO's step entirely
                      rather than shown in both places. His pre-population COUNT line above is
                      untouched - that tells him what was pre-filled into this form, which is a
                      different thing from the standing bank-change summary. */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formNoChanges}
                      onChange={e => { setFormNoChanges(e.target.checked); setActionError(null); }}
                    />
                    No changes this month
                  </label>
                  {!formNoChanges && (
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      {/* PAYROLLROWS: three repeatable categories, one row per person. The name
                          cell searches only THIS entity's employees (payroll-employee-search);
                          salary and bonus stay free text so real-world values like "45000 GBP"
                          or "pro-rata from the 12th" can be sent as written. */}
                      <div style={{ width: '100%' }}>
                        <PayrollRowCategory
                          label="New joiners"
                          rows={formJoiners}
                          onChange={setFormJoiners}
                          /* PAYSCREENFIX: `bonus` is GONE from the new joiner blank row - the
                             separate Bonus category below covers it, and carrying it here asked
                             the same question twice. The Bonus CATEGORY is untouched. */
                          blankRow={{ employee_id: null, employee_name: '', salary: '', start_date: '', additional_payment: '' }}
                          isDarkMode={isDarkMode}
                          textMuted={textMuted}
                          border={border}
                          renderCells={(row, patch) => (
                            <>
                              <EmployeeNameSearch
                                options={activeEmployees}
                                value={row.employee_name}
                                employeeId={row.employee_id}
                                onPick={patch}
                                placeholder="Search employee name"
                                isDarkMode={isDarkMode}
                              />
                              {/* PAYGRID: the start date sits in COLUMN 2, directly after
                                  the employee, so it lands in the same column as the
                                  leaver's last working date. A pure reorder of two
                                  sibling cells - no field name, value, handler or
                                  validation changes, and the submitted payload is built
                                  elsewhere and is unaffected. */}
                              {/* DATELABELBLUE: the date input showed only a bare dd/mm/yyyy
                                  placeholder, so nothing on screen said WHICH date it wanted.
                                  The visible caption states it exactly. The input's name, value,
                                  onChange, aria-label and style are untouched. */}
                              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: textMuted, minWidth: 0 }} htmlFor={`${a11yId}-start-date-first-working-day-patch-a`}>
                                Start date (first working day)
                                <input
                                  type="date" value={row.start_date || ''}
                                  onChange={e => patch({ start_date: e.target.value })}
                                  aria-label="Start date"
                                  style={{ fontSize: '13px', padding: '7px 9px', borderRadius: '6px', width: '100%', boxSizing: 'border-box', border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd'}`, background: isDarkMode ? '#1a2744' : '#fff', color: isDarkMode ? 'rgba(255,255,255,0.85)' : '#333' }}
                                />
                              </label>
                              
                              <input id={`${a11yId}-start-date-first-working-day-patch-a`}
                                type="text" value={row.salary || ''} maxLength={200}
                                onChange={e => patch({ salary: e.target.value })}
                                placeholder="Salary"
                                aria-label="Salary"
                                style={{ fontSize: '13px', padding: '7px 9px', borderRadius: '6px', width: '100%', boxSizing: 'border-box', border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd'}`, background: isDarkMode ? '#1a2744' : '#fff', color: isDarkMode ? 'rgba(255,255,255,0.85)' : '#333' }}
                              />
                              {/* PAYSCREENFIX: the new joiner Bonus field is REMOVED. The separate
                                  Bonus category is the one place a bonus is recorded. */}
                              <input
                                type="text" value={row.additional_payment || ''} maxLength={200}
                                onChange={e => patch({ additional_payment: e.target.value })}
                                placeholder="Additional one-off payment (optional)"
                                aria-label="Additional one-off payment"
                                style={{ fontSize: '13px', padding: '7px 9px', borderRadius: '6px', width: '100%', boxSizing: 'border-box', border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd'}`, background: isDarkMode ? '#1a2744' : '#fff', color: isDarkMode ? 'rgba(255,255,255,0.85)' : '#333' }}
                              />
                            </>
                          )}
                        />
                        <PayrollRowCategory
                          label="Leavers"
                          rows={formLeavers}
                          onChange={setFormLeavers}
                          blankRow={{ employee_id: null, employee_name: '', leaving_date: '', leaving_payment: '', accrued_holiday: '' }}
                          isDarkMode={isDarkMode}
                          textMuted={textMuted}
                          border={border}
                          renderCells={(row, patch) => (
                            <>
                              <EmployeeNameSearch
                                options={activeEmployees}
                                value={row.employee_name}
                                employeeId={row.employee_id}
                                onPick={patch}
                                placeholder="Search employee name"
                                isDarkMode={isDarkMode}
                              />
                              {/* DATELABELBLUE: same treatment as the joiner's start date. The
                                  caption says FINAL working day, which is the distinction that
                                  was invisible before. Field name and validation untouched. */}
                              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: textMuted, minWidth: 0 }} htmlFor={`${a11yId}-last-working-date-final-day-patch-ar`}>
                                Last working date (final day)
                                <input
                                  type="date" value={row.leaving_date || ''}
                                  onChange={e => patch({ leaving_date: e.target.value })}
                                  aria-label="Last working date"
                                  style={{ fontSize: '13px', padding: '7px 9px', borderRadius: '6px', width: '100%', boxSizing: 'border-box', border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd'}`, background: isDarkMode ? '#1a2744' : '#fff', color: isDarkMode ? 'rgba(255,255,255,0.85)' : '#333' }}
                                />
                              </label>
                              
                              <input id={`${a11yId}-last-working-date-final-day-patch-ar`}
                                type="text" value={row.leaving_payment || ''} maxLength={200}
                                onChange={e => patch({ leaving_payment: e.target.value })}
                                placeholder="Leaving payment (optional)"
                                aria-label="Leaving payment"
                                style={{ fontSize: '13px', padding: '7px 9px', borderRadius: '6px', width: '100%', boxSizing: 'border-box', border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd'}`, background: isDarkMode ? '#1a2744' : '#fff', color: isDarkMode ? 'rgba(255,255,255,0.85)' : '#333' }}
                              />
                              {/* ACCRUEDHOL: accrued holiday owed, typed in by the COO. A plain
                                  MANUAL entry - no calculation and no lookup against leave
                                  records, because nobody can yet say where that number would come
                                  from. `inputMode="decimal"` offers a numeric keypad for the
                                  common case without a `type="number"` spinner rejecting an entry
                                  like "3.5 days"; the value travels as text, exactly like every
                                  other amount cell on this form. Same flex basis and minWidth as
                                  its two neighbours, so the row wraps as one consistent set. */}
                              <input
                                type="text" inputMode="decimal" value={row.accrued_holiday || ''} maxLength={200}
                                onChange={e => patch({ accrued_holiday: e.target.value })}
                                placeholder="Accrued holiday (optional)"
                                aria-label="Accrued holiday"
                                style={{ fontSize: '13px', padding: '7px 9px', borderRadius: '6px', width: '100%', boxSizing: 'border-box', border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd'}`, background: isDarkMode ? '#1a2744' : '#fff', color: isDarkMode ? 'rgba(255,255,255,0.85)' : '#333' }}
                              />
                            </>
                          )}
                        />
                        <PayrollRowCategory
                          label="Salary changes"
                          rows={formSalaryRows}
                          onChange={setFormSalaryRows}
                          blankRow={{ employee_id: null, employee_name: '', new_salary: '', bonus: '' }}
                          isDarkMode={isDarkMode}
                          textMuted={textMuted}
                          border={border}
                          renderCells={(row, patch) => (
                            <>
                              <EmployeeNameSearch
                                options={activeEmployees}
                                value={row.employee_name}
                                employeeId={row.employee_id}
                                onPick={patch}
                                placeholder="Search employee name"
                                isDarkMode={isDarkMode}
                              />
                              <input
                                type="text" value={row.new_salary || ''} maxLength={200}
                                onChange={e => patch({ new_salary: e.target.value })}
                                placeholder="New salary"
                                aria-label="New salary"
                                style={{ fontSize: '13px', padding: '7px 9px', borderRadius: '6px', width: '100%', boxSizing: 'border-box', border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd'}`, background: isDarkMode ? '#1a2744' : '#fff', color: isDarkMode ? 'rgba(255,255,255,0.85)' : '#333' }}
                              />
                              {/* PAYFIELDS: this comment box carries maternity and paternity
                                  leave as free text, confirmed on the call - not a bonus. */}
                              <input
                                type="text" value={row.bonus || ''} maxLength={200}
                                onChange={e => patch({ bonus: e.target.value })}
                                placeholder="Comment, e.g. maternity or paternity leave (optional)"
                                aria-label="Salary change comment"
                                style={{ fontSize: '13px', padding: '7px 9px', borderRadius: '6px', width: '100%', boxSizing: 'border-box', border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd'}`, background: isDarkMode ? '#1a2744' : '#fff', color: isDarkMode ? 'rgba(255,255,255,0.85)' : '#333' }}
                              />
                              {/* The MATB1 form or equivalent. Uploaded through the portal's ONE
                                  existing document endpoint; only its id is sent with the row. */}
                              <input
                                type="file"
                                aria-label="Salary change document"
                                disabled={!row.employee_id}
                                title={row.employee_id ? undefined : 'Pick the employee from the search to attach a document'}
                                onChange={e => onRowFile(e.target.files[0] || null, patch, row)}
                                style={{ fontSize: '12px', minWidth: 0, color: textMuted }}
                              />
                            </>
                          )}
                        />
                        {/* PAYROLLBONUS: the FOURTH section, at the same level as the other three
                            and built from the same PayrollRowCategory component. For a bonus or
                            premium paid OUTSIDE of a salary change - the bonus cell inside a
                            Salary changes row above is untouched and still covers a bonus that
                            comes WITH one. */}
                        <PayrollRowCategory
                          label="Bonus"
                          addLabel="bonus"
                          rows={formBonusRows}
                          onChange={setFormBonusRows}
                          blankRow={{ employee_id: null, employee_name: '', amount: '', kind: '' }}
                          isDarkMode={isDarkMode}
                          textMuted={textMuted}
                          border={border}
                          renderCells={(row, patch) => (
                            <>
                              <EmployeeNameSearch
                                options={activeEmployees}
                                value={row.employee_name}
                                employeeId={row.employee_id}
                                onPick={patch}
                                placeholder="Search employee name"
                                isDarkMode={isDarkMode}
                              />
                              {/* PAYFIELDS: the three fixed options confirmed on the call. */}
                              <select
                                value={row.kind || ''}
                                onChange={e => patch({ kind: e.target.value })}
                                aria-label="Bonus type"
                                style={{ fontSize: '13px', padding: '7px 9px', borderRadius: '6px', width: '100%', boxSizing: 'border-box', border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd'}`, background: isDarkMode ? '#1a2744' : '#fff', color: isDarkMode ? 'rgba(255,255,255,0.85)' : '#333' }}
                              >
                                <option value="">Type...</option>
                                {PAYROLL_BONUS_KINDS.map(k => <option key={k} value={k}>{k}</option>)}
                              </select>
                              <input
                                type="text" value={row.amount || ''} maxLength={200}
                                onChange={e => patch({ amount: e.target.value })}
                                placeholder="Amount"
                                aria-label="Bonus amount"
                                style={{ fontSize: '13px', padding: '7px 9px', borderRadius: '6px', width: '100%', boxSizing: 'border-box', border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd'}`, background: isDarkMode ? '#1a2744' : '#fff', color: isDarkMode ? 'rgba(255,255,255,0.85)' : '#333' }}
                              />
                            </>
                          )}
                        />
                        {/* PAYFIELDS: interns get their OWN category - they do not follow the
                            normal joiner or leaver flow, so forcing them into one of those rows
                            would misrepresent what is being paid and why. */}
                        <PayrollRowCategory
                          label="Intern payments"
                          addLabel="intern payment"
                          rows={formInternRows}
                          onChange={setFormInternRows}
                          blankRow={{ employee_id: null, employee_name: '', amount: '' }}
                          isDarkMode={isDarkMode}
                          textMuted={textMuted}
                          border={border}
                          renderCells={(row, patch) => (
                            <>
                              <EmployeeNameSearch
                                options={activeEmployees}
                                value={row.employee_name}
                                employeeId={row.employee_id}
                                onPick={patch}
                                placeholder="Search employee name"
                                isDarkMode={isDarkMode}
                              />
                              <input
                                type="text" value={row.amount || ''} maxLength={200}
                                onChange={e => patch({ amount: e.target.value })}
                                placeholder="One-off payment"
                                aria-label="Intern payment amount"
                                style={{ fontSize: '13px', padding: '7px 9px', borderRadius: '6px', width: '100%', boxSizing: 'border-box', border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd'}`, background: isDarkMode ? '#1a2744' : '#fff', color: isDarkMode ? 'rgba(255,255,255,0.85)' : '#333' }}
                              />
                            </>
                          )}
                        />
                        {/* PAYFIELDS: ONE general comment box at the end of the whole form, for
                            anything that does not fit the categories above. Not per-category. */}
                        <div style={{ marginTop: '4px' }}>
                          <div style={{ fontSize: '12px', color: textMuted, marginBottom: '4px' }}>
                            Comment
                          </div>
                          <textarea
                            value={formGeneralComment}
                            onChange={e => setFormGeneralComment(e.target.value)}
                            maxLength={4000}
                            rows={3}
                            placeholder="Anything that does not fit the categories above (optional)"
                            aria-label="General comment"
                            style={{
                              width: '100%', boxSizing: 'border-box', fontSize: '13px',
                              padding: '7px 9px', borderRadius: '6px', resize: 'vertical',
                              border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd'}`,
                              background: isDarkMode ? '#1a2744' : '#fff',
                              color: isDarkMode ? 'rgba(255,255,255,0.85)' : '#333',
                              fontFamily: 'inherit',
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <div>
                    <button
                      onClick={handlePayrollChanges}
                      disabled={actionLoading}
                      style={{ background: '#1a2744', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: 600, cursor: actionLoading ? 'wait' : 'pointer' }}
                    >
                      {actionLoading ? 'Saving…' : 'Send to the Financial Controller'}
                    </button>
                  </div>
                </div>
              ) : currentStep.key === 'three_offers_received' ? (
                /* GLIBRANCH: gateway - Yes proceeds, No loops back to contacting brokers. */
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ fontSize: '13px' }}>Have all 3 broker offers been received?</div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => handleGliBids('yes')}
                      disabled={actionLoading}
                      style={{ background: '#1a2744', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: 600, cursor: actionLoading ? 'wait' : 'pointer' }}
                    >
                      {actionLoading ? 'Saving…' : 'Yes, all 3 received'}
                    </button>
                    <button
                      onClick={() => handleGliBids('no')}
                      disabled={actionLoading}
                      style={{ background: 'transparent', color: '#c8a84b', border: '1px solid #c8a84b', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: 600, cursor: actionLoading ? 'wait' : 'pointer' }}
                    >
                      {actionLoading ? 'Saving…' : 'Not yet - keep chasing'}
                    </button>
                  </div>
                </div>
              ) : currentStep.key === 'select_broker' ? (
                /* GLIBRANCH: broker selection form - broker + premium. */
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: textMuted }}>
                    Broker selected
                    <input
                      type="text"
                      value={formBroker}
                      onChange={e => setFormBroker(e.target.value)}
                      maxLength={200}
                      placeholder="Broker name"
                      style={{ fontSize: '13px', padding: '7px 9px', borderRadius: '6px', minWidth: '180px',
                        border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd'}`,
                        background: isDarkMode ? '#1a2744' : '#fff', color: isDarkMode ? 'rgba(255,255,255,0.85)' : '#333' }}
                    />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: textMuted }}>
                    Premium amount
                    <input
                      type="text"
                      value={formPremium}
                      onChange={e => setFormPremium(e.target.value)}
                      maxLength={100}
                      placeholder="e.g. 12500 GBP"
                      style={{ fontSize: '13px', padding: '7px 9px', borderRadius: '6px', minWidth: '160px',
                        border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd'}`,
                        background: isDarkMode ? '#1a2744' : '#fff', color: isDarkMode ? 'rgba(255,255,255,0.85)' : '#333' }}
                    />
                  </label>
                  <button
                    onClick={handleGliBroker}
                    disabled={actionLoading}
                    style={{ background: '#1a2744', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: 600, cursor: actionLoading ? 'wait' : 'pointer' }}
                  >
                    {actionLoading ? 'Saving…' : 'Save & mark complete'}
                  </button>
                </div>
              ) : currentStep.key === 'approve_payroll_payment' ? (
                /* PAYROLLFINALAPPROVE step 3 (COO): the FC's processing is done, so he sees what
                   was sent at step 1 for context, then confirms the payments have been made with
                   the date and bank reference. An explicit branch BEFORE the final else, which is
                   Pension's NEST form - without it this step would render the wrong form. */
                (() => {
                  const sent = (selected.steps || []).find(s => s.key === 'confirm_payroll_changes');
                  const fd = sent?.form_data || {};
                  // PAYATTACH: the FC's OWN processing step - her provider report and comment had
                  // NO display anywhere, so the COO approved a payroll run without being able to
                  // see, let alone open, the reports she attached. Read from the same steps array
                  // the block above already uses; no new fetch.
                  const processed = (selected.steps || []).find(s => s.key === 'process_payroll');
                  const pfd = processed?.form_data || {};
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div style={{ fontSize: '12px', color: textMuted }}>
                        This month's changes, sent by {sent?.actor_name || 'the COO'} and processed
                        with the payroll provider:
                      </div>
                      <div style={{ background: cardBg, border: `1px solid ${border}`, borderRadius: '8px', padding: '10px 12px', fontSize: '13px' }}>
                        {fd.no_changes ? (
                          <div>No changes this month</div>
                        ) : (
                          <>
                            {/* PAYROLLROWS: one readable line per person per category, so she
                                receives named values instead of a paragraph to interpret. */}
                            <PayrollChangeSummary fd={fd} textMuted={textMuted} instanceId={selected.id} />
                          </>
                        )}
                      </div>
                      {/* PAYATTACH: what the Financial Controller recorded on her own step - her
                          comment and, crucially, the provider reports she attached, now openable
                          through the payroll attachment route. The whole block is omitted when she
                          recorded neither, rather than showing an empty heading. */}
                      {(pfd.processing_notes || pfd.document_id) ? (
                        <div style={{ background: cardBg, border: `1px solid ${border}`, borderRadius: '8px', padding: '10px 12px', fontSize: '13px' }}>
                          <div style={{ color: textMuted, marginBottom: '4px' }}>
                            Recorded by {processed?.actor_name || 'the Financial Controller'}:
                          </div>
                          {pfd.processing_notes ? (
                            <div style={{ marginLeft: '10px' }}>{pfd.processing_notes}</div>
                          ) : null}
                          {pfd.document_id ? (
                            <div style={{ marginLeft: '10px' }}>
                              Payroll provider reports
                              <PayrollAttachmentLink instanceId={selected.id} documentId={pfd.document_id} />
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
                        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: textMuted }}>
                          Payment date
                          <input
                            type="date"
                            aria-label="Payment date"
                            value={formPayDate}
                            onChange={e => { setFormPayDate(e.target.value); setActionError(null); }}
                            style={{
                              fontSize: '13px', padding: '7px 9px', borderRadius: '6px',
                              border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd'}`,
                              background: isDarkMode ? '#1a2744' : '#fff',
                              color: isDarkMode ? 'rgba(255,255,255,0.85)' : '#333',
                            }}
                          />
                        </label>
                        {/* PAYFIELDS: the payment reference field is GONE. Only the date
                            (required) and this optional comment remain. */}
                        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: textMuted }}>
                          Comment (optional)
                          <input
                            type="text"
                            value={formPayComment}
                            onChange={e => { setFormPayComment(e.target.value); setActionError(null); }}
                            maxLength={4000}
                            aria-label="Approval comment"
                            style={{
                              fontSize: '13px', padding: '7px 9px', borderRadius: '6px', minWidth: '200px',
                              border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd'}`,
                              background: isDarkMode ? '#1a2744' : '#fff',
                              color: isDarkMode ? 'rgba(255,255,255,0.85)' : '#333',
                            }}
                          />
                        </label>
                        <button
                          onClick={handlePayrollPaymentApproval}
                          disabled={actionLoading}
                          style={{ background: '#1a2744', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: 600, cursor: actionLoading ? 'wait' : 'pointer' }}
                        >
                          {actionLoading ? 'Saving…' : 'Approve & confirm payment made'}
                        </button>
                      </div>
                    </div>
                  );
                })()
              ) : currentStep.key === 'process_payroll' ? (
              /* PAYSCREENFIX: MOVED into the requires_form chain. process_payroll is a FORM step
                 (requires_form=True), so while this branch sat OUTSIDE that chain it was
                 UNREACHABLE: the step fell through to the final else below, which is Pension's
                 NEST form, and rendered Submission date + NEST reference instead of its own
                 panel. That is why the bank summary, the provider upload and the comment field
                 built for this step never appeared on screen. Same pattern the
                 approve_payroll_payment branch above already uses - a named case BEFORE the
                 catch-all else. */
              /* PAYROLLREVERSE step 2 (Financial Controller): read-only display of what the COO
                 sent at step 1 - this is the handoff that carries his data to her - then a plain
                 "Confirm processed" mark-complete. She coordinates with the external payroll
                 provider and prepares the payment outside the portal, then confirms here; no
                 extra structured data is captured, mirroring how the COO's step 2 worked before
                 the order was corrected. */
              (() => {
                const sent = (selected.steps || []).find(s => s.key === 'confirm_payroll_changes');
                const fd = sent?.form_data || {};
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ fontSize: '12px', color: textMuted }}>
                      Sent by {sent?.actor_name || 'the COO'}:
                    </div>
                    <div style={{ background: cardBg, border: `1px solid ${border}`, borderRadius: '8px', padding: '10px 12px', fontSize: '13px' }}>
                      {fd.no_changes ? (
                        <div>No changes this month</div>
                      ) : (
                        <>
                          {/* PAYROLLROWS: same readable per-row rendering as the FC's step. */}
                          <PayrollChangeSummary fd={fd} textMuted={textMuted} instanceId={selected.id} />
                        </>
                      )}
                    </div>
                    {/* PAYFIELDS: the bank account change summary for the period, shown to her
                        AUTOMATICALLY rather than something she has to search for and copy in.
                        Reuses the EXISTING bank change detection (payroll-prepopulate's
                        bank_changes, itself fed by the BANKCHANGE log) - no second mechanism.
                        payrollPrefill is already fetched when this instance opens. */}
                    <div style={{ background: cardBg, border: `1px solid ${border}`, borderRadius: '8px', padding: '10px 12px', fontSize: '13px' }}>
                      <div style={{ color: textMuted, marginBottom: '4px' }}>
                        Bank account changes this period:
                      </div>
                      {payrollPrefill === null ? (
                        <div style={{ color: textMuted }}>Loading…</div>
                      ) : (payrollPrefill.bank_changes || []).length === 0 ? (
                        <div style={{ color: textMuted }}>None recorded this period.</div>
                      ) : (
                        (payrollPrefill.bank_changes || []).map((b, i) => (
                          /* BANKDEDUPE: ONE CARD PER PERSON. This was an indented inline list, so
                             several people ran together as one block of text with nothing marking
                             where one person ended and the next began. Each entry now sits in its
                             own bordered card, reusing the SAME pattern this screen already uses
                             for its outer panels (1px solid border, cardBg) at the 6px radius this
                             file already uses for elements nested INSIDE an 8px card, rather than
                             a new style invented here.

                             `detail` carries the employee's REAL current bank details, resolved
                             server side. The "Changed: rib_iban" line that used to sit below it is
                             GONE: it printed a raw internal column name that told the reader
                             nothing they could act on, and the real account sits directly above
                             it. An employee with nothing on file still says so explicitly rather
                             than rendering an empty line. */
                          <div
                            key={b.id || i}
                            style={{
                              background: cardBg,
                              border: `1px solid ${border}`,
                              borderRadius: '6px',
                              padding: '8px 10px',
                              marginBottom: i === (payrollPrefill.bank_changes || []).length - 1 ? 0 : '6px',
                            }}
                          >
                            <div style={{ fontWeight: 600, marginBottom: '3px' }}>{b.name}</div>
                            <div>{b.detail || 'No bank details on file'}</div>
                          </div>
                        ))
                      )}
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: textMuted, marginBottom: '4px' }}>
                        {/* REPORTREQ: the upload is MANDATORY to complete this step. The label
                            said "(optional)" and the control could be skipped, which is what was
                            observed on screen twice. The COMMENT below stays optional. */}
                        Payroll provider reports (required)
                      </div>
                      <input
                        type="file"
                        aria-label="Payroll provider reports"
                        onChange={e => setFormProcessingDoc(e.target.files[0] || null)}
                        style={{ fontSize: '13px', color: isDarkMode ? 'rgba(255,255,255,0.7)' : '#333' }}
                      />
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: textMuted, marginBottom: '4px' }}>
                        Comment (optional)
                      </div>
                      <textarea
                        value={formProcessingNotes}
                        onChange={e => setFormProcessingNotes(e.target.value)}
                        maxLength={4000}
                        rows={3}
                        placeholder="Anything worth recording about this month's processing"
                        aria-label="Processing comment"
                        style={{
                          width: '100%', boxSizing: 'border-box', fontSize: '13px',
                          padding: '7px 9px', borderRadius: '6px', resize: 'vertical',
                          border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd'}`,
                          background: isDarkMode ? '#1a2744' : '#fff',
                          color: isDarkMode ? 'rgba(255,255,255,0.85)' : '#333',
                          fontFamily: 'inherit',
                        }}
                      />
                    </div>
                    <div>
                      <button
                        onClick={handlePayrollProcessing}
                        /* REPORTREQ: the server refuses a completion with no upload (400), so the
                           control is disabled to match and she is never offered an action the API
                           would reject. The server remains the real enforcement point. */
                        disabled={actionLoading || !formProcessingDoc}
                        title={formProcessingDoc ? undefined : 'Attach the payroll provider reports first'}
                        style={{ background: '#1a2744', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: 600, cursor: actionLoading ? 'wait' : 'pointer' }}
                      >
                        {actionLoading ? 'Completing…' : 'Confirm processed'}
                      </button>
                    </div>
                  </div>
                );
              })()
              ) : (
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: textMuted }}>
                  Submission date
                  <input
                    type="date"
                    value={formDate}
                    onChange={e => setFormDate(e.target.value)}
                    style={{
                      fontSize: '13px', padding: '7px 9px', borderRadius: '6px',
                      border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd'}`,
                      background: isDarkMode ? '#1a2744' : '#fff',
                      color: isDarkMode ? 'rgba(255,255,255,0.85)' : '#333',
                    }}
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: textMuted }}>
                  NEST reference
                  <input
                    type="text"
                    value={formRef}
                    onChange={e => setFormRef(e.target.value)}
                    maxLength={100}
                    placeholder="Confirmation number"
                    style={{
                      fontSize: '13px', padding: '7px 9px', borderRadius: '6px', minWidth: '200px',
                      border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd'}`,
                      background: isDarkMode ? '#1a2744' : '#fff',
                      color: isDarkMode ? 'rgba(255,255,255,0.85)' : '#333',
                    }}
                  />
                </label>
                <button
                  onClick={handleFormComplete}
                  disabled={actionLoading}
                  style={{ background: '#1a2744', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: 600, cursor: actionLoading ? 'wait' : 'pointer' }}
                >
                  {actionLoading ? 'Saving…' : 'Save & mark complete'}
                </button>
              </div>
              )
            ) : currentStep.requires_upload ? (
              /* PENSIONCOMMENT: the upload row is now a COLUMN so an optional comment box can sit
                 between the file control and the button. The file input and the button themselves
                 are unchanged - same handlers, same labels, same styles; only the wrapper's
                 direction changed, and the original row is preserved as the inner flex div below
                 so the control and button still sit side by side exactly as before. */
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  {/* EVIDENCETYPES: the evidence types this step accepts, matching the
                      `accept=".pdf,.jpg,.jpeg,.png"` convention five other uploads in the portal
                      already use (DocumentUpload, ComplianceModule, LeaverForm, SponsorForm,
                      PolicyRepository), extended with the Office types a provider's own return or
                      schedule arrives as.

                      ADVISORY ONLY, and deliberately not the enforcement point: `accept` filters
                      the OS picker and is trivially bypassed by a drag and drop or a direct API
                      call. The real rule is EVIDENCE_UPLOAD_EXTENSIONS on the endpoint, which is
                      what actually refuses an mp4. This exists so the wrong file is hard to pick
                      in the first place, not so the right one is guaranteed. */}
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                    onChange={e => setActionFile(e.target.files[0] || null)}
                    style={{ fontSize: '13px', color: isDarkMode ? 'rgba(255,255,255,0.7)' : '#333' }}
                  />
                </div>
                {/* Same shape as the provider report comment on "Process payroll with provider":
                    same "(optional)" label wording, same 4000 cap, same 3 rows, same styling. */}
                <div>
                  <div style={{ fontSize: '12px', color: textMuted, marginBottom: '4px' }}>
                    Comment (optional)
                  </div>
                  <textarea
                    value={actionComment}
                    onChange={e => setActionComment(e.target.value)}
                    maxLength={4000}
                    rows={3}
                    placeholder="Anything worth recording about this submission"
                    aria-label="Comment"
                    style={{
                      width: '100%', boxSizing: 'border-box', fontSize: '13px',
                      padding: '7px 9px', borderRadius: '6px', resize: 'vertical',
                      border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd'}`,
                      background: isDarkMode ? '#1a2744' : '#fff',
                      color: isDarkMode ? 'rgba(255,255,255,0.85)' : '#333',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>
                <div>
                  <button
                    onClick={handleUploadAndComplete}
                    disabled={actionLoading}
                    style={{ background: '#1a2744', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: 600, cursor: actionLoading ? 'wait' : 'pointer' }}
                  >
                    {actionLoading ? 'Uploading…' : 'Upload & mark complete'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={handleCompleteStep}
                disabled={actionLoading}
                style={{ background: '#1a2744', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: 600, cursor: actionLoading ? 'wait' : 'pointer' }}
              >
                {actionLoading ? 'Completing…' : 'Mark complete'}
              </button>
            )}
            {/* WFREJECT: an approval-gate step on Management Accounts / Partnership Tax Return can
                be SENT BACK instead of approved, with a mandatory reason. Rendered after the
                completion control (not inside any one branch of it) so it appears alongside
                whichever completion control the step uses. The condition mirrors the backend's own
                `_rejectable_step` exactly: the two workflow types, and is_approval_gate read from
                the serialized step - never a hard-coded step key. */}
            {isRejectableStep && (
              <div style={{ marginTop: '12px', borderTop: `1px solid ${border}`, paddingTop: '12px' }}>
                {!rejectOpen ? (
                  <button
                    onClick={() => { setRejectOpen(true); setActionError(null); }}
                    disabled={actionLoading}
                    style={{ background: 'transparent', color: '#A63A3A', border: '1px solid #A63A3A', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: 600, cursor: actionLoading ? 'wait' : 'pointer' }}
                  >
                    Send back for resubmission
                  </button>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: textMuted }}>
                      Reason for sending this back (required)
                      <textarea
                        value={rejectReason}
                        onChange={e => { setRejectReason(e.target.value); setActionError(null); }}
                        maxLength={2000}
                        rows={3}
                        placeholder="Explain what needs to change before this can be approved"
                        style={{
                          fontSize: '13px', padding: '7px 9px', borderRadius: '6px', resize: 'vertical',
                          border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd'}`,
                          background: isDarkMode ? '#1a2744' : '#fff',
                          color: isDarkMode ? 'rgba(255,255,255,0.85)' : '#333',
                          fontFamily: 'inherit',
                        }}
                      />
                    </label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        onClick={handleRejectStep}
                        disabled={actionLoading}
                        style={{ background: '#A63A3A', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: 600, cursor: actionLoading ? 'wait' : 'pointer' }}
                      >
                        {actionLoading ? 'Sending back…' : 'Confirm send back'}
                      </button>
                      <button
                        onClick={() => { setRejectOpen(false); setRejectReason(''); setActionError(null); }}
                        disabled={actionLoading}
                        style={{ background: 'transparent', color: textMuted, border: `1px solid ${border}`, borderRadius: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {onBack && (
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#c8a84b', fontSize: '13px', cursor: 'pointer', marginBottom: '16px', padding: 0 }}>
          ← Back
        </button>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ margin: 0, fontSize: '18px' }}>{title || 'Workflows'}</h2>
        {(currentUserRole === 'coo' || currentUserRole === 'admin') && isTestAccount() && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* TESTCREATEGEN: a selector rather than 11 buttons - this is a QA-only control and
                11 buttons would dominate the header for every other reader of this screen. One
                dropdown + one button keeps the existing single-click flow (FCA Quarterly stays
                the default) while reaching all 11 types. */}
            <select
              value={testCreateType}
              onChange={e => { setTestCreateType(e.target.value); setTestCreateMsg(null); }}
              disabled={testCreateLoading}
              aria-label="Workflow type to create a test instance for"
              style={{
                background: 'none', border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd'}`,
                borderRadius: '6px', padding: '5px 8px', fontSize: '12px', cursor: 'pointer',
                color: isDarkMode ? 'rgba(255,255,255,0.7)' : '#475467',
                backgroundColor: isDarkMode ? '#1a2744' : '#fff', maxWidth: '190px',
              }}
            >
              {TEST_CREATE_TYPES.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <button
              onClick={handleTestCreate}
              disabled={testCreateLoading}
              title="QA only - force-creates an instance of the selected workflow type for its current period, using the same logic as the real scheduled job"
              style={{
                background: 'none', border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.25)' : '#d0d5dd'}`,
                borderRadius: '6px', padding: '5px 12px', fontSize: '12px', cursor: 'pointer',
                color: isDarkMode ? 'rgba(255,255,255,0.7)' : '#475467',
              }}
            >
              {testCreateLoading ? 'Creating…' : 'Create test instance'}
            </button>
            {testCreateMsg && <span style={{ fontSize: '12px', color: textMuted }}>{testCreateMsg}</span>}
          </div>
        )}
      </div>
      {loading && <div style={{ fontSize: '13px', color: textMuted }}>Loading...</div>}
      {error && <div style={{ fontSize: '13px', color: '#A63A3A' }}>{error}</div>}
      {detailLoading && <div style={{ fontSize: '13px', color: textMuted }}>Loading workflow...</div>}
      {!loading && !error && instances.some(wi => wi.status === 'skipped_backfill') && (
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: textMuted, marginBottom: '8px' }}>
          <input type="checkbox" checked={showOlder} onChange={e => setShowOlder(e.target.checked)}
            data-testid="wf-show-older" />
          {t('workflowList.showOlder')}
        </label>
      )}
      {!loading && !error && shownInstances.length === 0 && (
        <div style={{ fontSize: '13px', color: textMuted, fontStyle: 'italic' }}>No workflows to show.</div>
      )}
      {!loading && shownInstances.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {/* WFSORT: sorted at the render site rather than by mutating `instances`, so the
              fetched state stays exactly what the backend returned and nothing downstream of it
              changes. sortInstances copies before sorting, so the state array is never mutated. */}
          {sortInstances(shownInstances).map(wi => (
            <div
              key={wi.id}
              onClick={() => openInstance(wi.id)}
              style={{
                background: cardBg, border: `1px solid ${border}`, borderRadius: '8px',
                padding: '14px 18px', cursor: 'pointer', display: 'flex',
                justifyContent: 'space-between', alignItems: 'center',
              }}
            >
              <div>
                <div style={{ fontSize: '14px', fontWeight: 600 }}>
                  {/* DASHFIX2: plain hyphen, never an em/en dash - the codebase's standing rule.
                      Matches the detail view above, which already uses a middot separator. */}
                  {/* PAYENTITY: the entity label distinguishes the per-entity Payroll instances,
                      which otherwise all read "Payroll - 2026-09" in one list. */}
                  {wi.workflow_label}{wi.entity_key ? ` (${ENTITY_LABEL[wi.entity_key] || wi.entity_key})` : ''}{wi.reference_period ? ` - ${wi.reference_period}` : ''}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                  {/* STATUSPALETTE: same pill as the detail header, kept as a single source of
                      status colour for this whole module. */}
                  <StatusPill
                    statusKey={statusPillKey(wi)}
                    label={statusPillKey(wi) === 'escalated' ? 'Escalated' : (STATUS_LABEL[wi.status] || wi.status)}
                    isDarkMode={isDarkMode}
                  />
                  {wi.paused && <PausedPill isDarkMode={isDarkMode} />}
                  {wi.current_assignee_name && <span style={{ fontSize: '12px', color: textMuted }}>{wi.current_assignee_name}</span>}
                </div>
              </div>
              {wi.due_date && (
                <div style={{ fontSize: '12px', color: textMuted }}>
                  Due {toDisplayDate(wi.due_date).toLocaleDateString()}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
